import React, { useCallback, useEffect, useState, useRef } from "react";
import "./App.css";
import LifeAdminView, { LifeAdminToday } from "./LifeAdminView";
import { apiGet, apiPost, apiPatch, apiDelete, apiPostBlob } from "./api";

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function renderMarkdown(content) {
  const lines = String(content || "").split(/\r?\n/);
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i].trim();

    if (!line) {
      i += 1;
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      const level = Math.min(heading[1].length, 3);
      const Tag = `h${level + 2}`;
      blocks.push(<Tag key={`h-${i}`}>{renderInline(heading[2])}</Tag>);
      i += 1;
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^[-*]\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ul key={`ul-${i}`}>
          {items.map((item, index) => <li key={index}>{renderInline(item)}</li>)}
        </ul>
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        items.push(lines[i].trim().replace(/^\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push(
        <ol key={`ol-${i}`}>
          {items.map((item, index) => <li key={index}>{renderInline(item)}</li>)}
        </ol>
      );
      continue;
    }

    const paragraph = [line];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^(#{1,3})\s+/.test(lines[i].trim()) &&
      !/^[-*]\s+/.test(lines[i].trim()) &&
      !/^\d+\.\s+/.test(lines[i].trim())
    ) {
      paragraph.push(lines[i].trim());
      i += 1;
    }
    blocks.push(<p key={`p-${i}`}>{renderInline(paragraph.join(" "))}</p>);
  }

  return blocks;
}



function unregisterNorthstarPushWorkers() {
  if (!("serviceWorker" in navigator)) return;
  navigator.serviceWorker.getRegistrations?.().then(registrations => {
    registrations.forEach(registration => {
      const scriptUrl = registration.active?.scriptURL || registration.waiting?.scriptURL || registration.installing?.scriptURL || "";
      if (scriptUrl.includes("push-sw.js")) {
        registration.unregister().catch(() => {});
      }
    });
  }).catch(() => {});
}
function getMainBundleUrl(root = document) {
  const scripts = Array.from(root.querySelectorAll("script[src]"));
  return scripts
    .map(script => new URL(script.getAttribute("src"), window.location.origin).href)
    .find(src => /\/static\/js\/main\.[^/]+\.js(?:\?|$)/.test(src)) || "";
}

function reloadForUpdate() {
  const url = new URL(window.location.href);
  url.searchParams.set("northstar_updated", Date.now().toString());
  window.location.replace(url.toString());
}

function getNotificationTitle(notification) {
  return notification.title || notification.subject || notification.type || "Reminder";
}

function getNotificationBody(notification) {
  return notification.body || notification.message || notification.content || notification.text || "";
}


function isDailyBriefingNotification(notification) {
  return /daily briefing/i.test(getNotificationTitle(notification)) || notification?.type === "daily_briefing";
}

function parseDailyBriefing(body) {
  const text = String(body || "").replace(/\s+/g, " ").trim();
  const labels = [
    { key: "calendar", label: "Calendar" },
    { key: "tasks", label: "Tasks and reminders" },
    { key: "email", label: "Email signals" },
    { key: "context", label: "Useful context" },
  ];
  const positions = labels.map(item => ({ ...item, index: text.indexOf(item.label + ":") })).filter(item => item.index >= 0).sort((x, y) => x.index - y.index);
  const greetingEnd = positions.length ? positions[0].index : text.length;
  const greeting = text.slice(0, greetingEnd).trim();
  const sections = {};
  positions.forEach((item, index) => {
    const start = item.index + item.label.length + 1;
    const end = positions[index + 1]?.index ?? text.length;
    sections[item.key] = text.slice(start, end).trim().replace(/^[-–]\s*/, "");
  });
  const toItems = value => String(value || "").split(/\s+[-–]\s+/).map(item => item.trim()).filter(Boolean);
  return {
    greeting: greeting.replace(/^Good (morning|afternoon|evening) [^.]+\.\s*/i, "").trim(),
    calendar: toItems(sections.calendar),
    tasks: toItems(sections.tasks),
    email: toItems(sections.email).filter(item => !/amazon|order confirmation|delivered|dispatch|github.*run (failed|succeeded)/i.test(item)),
  };
}

function BriefingNotificationBody({ notification }) {
  const briefing = parseDailyBriefing(getNotificationBody(notification));
  const sections = [
    { key: "calendar", title: "Today", items: briefing.calendar },
    { key: "tasks", title: "Tasks & reminders", items: briefing.tasks },
    { key: "email", title: "Needs a reply", items: briefing.email },
  ].filter(section => section.items.length && !/^nothing (due|waiting)/i.test(section.items.join(" ")));
  return (
    <span className="ns-briefing-summary">
      {briefing.greeting && <span className="ns-briefing-intro">{briefing.greeting}</span>}
      {sections.map(section => (
        <span className="ns-briefing-section" key={section.key}>
          <b>{section.title}</b>
          {section.items.slice(0, 4).map((item, index) => <span className="ns-briefing-line" key={index}>{item}</span>)}
          {section.items.length > 4 && <small>+{section.items.length - 4} more</small>}
        </span>
      ))}
      {!sections.length && <span className="ns-briefing-clear">Nothing needs your attention right now.</span>}
    </span>
  );
}

function groupReminders(reminders) {
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const inSevenDays = new Date(startToday); inSevenDays.setDate(inSevenDays.getDate() + 7);
  const inThirtyDays = new Date(startToday); inThirtyDays.setDate(inThirtyDays.getDate() + 30);
  const startNextYear = new Date(now.getFullYear() + 1, 0, 1);
  const groups = [
    { key: "due", title: "Due now", reminders: [], open: true },
    { key: "week", title: "Next 7 days", reminders: [], open: true },
    { key: "month", title: "Next 30 days", reminders: [], open: true },
    { key: "year", title: "Rest of this year", reminders: [], open: false },
  ];
  reminders.forEach(reminder => {
    const date = new Date(reminder.next_run_at || reminder.due_at);
    if (Number.isNaN(date.getTime()) || date < inSevenDays) groups[date < startToday ? 0 : 1].reminders.push(reminder);
    else if (date < inThirtyDays) groups[2].reminders.push(reminder);
    else if (date < startNextYear) groups[3].reminders.push(reminder);
    else {
      const year = date.getFullYear();
      let group = groups.find(item => item.key === String(year));
      if (!group) {
        group = { key: String(year), title: String(year), reminders: [], open: false };
        groups.push(group);
      }
      group.reminders.push(reminder);
    }
  });
  return groups.filter(group => group.reminders.length);
}

function priorityActionLabel(action) {
  return {
    complete: "Done",
    tomorrow: "Tomorrow",
    next_week: "Next week",
    review_reply: "Review reply",
    snooze: "Snooze",
    ignore: "Ignore",
    directions: "Directions",
    check_in: "Check in",
    follow_up: "Draft follow-up",
  }[action] || action;
}

function isNotificationUnread(notification) {
  if (notification.read_at || notification.readAt) return false;
  if (notification.is_read === true || notification.read === true) return false;
  return true;
}

function formatNotificationTime(notification, timeFormat = "24") {
  const raw = notification.remind_at || notification.reminder_at || notification.scheduled_for || notification.created_at || notification.createdAt;
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: normalizeTimeFormat(timeFormat) === "12",
  });
}

function toDateTimeLocalValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function formatReminderDate(value, timeFormat = "24") {
  if (!value) return "Time not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Time not set";
  return date.toLocaleString("en-GB", {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: normalizeTimeFormat(timeFormat) === "12",
  });
}
function stripMarkdownForSpeech(content) {
  return String(content || "")
    .replace(/```[\s\S]*?```/g, "")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/[#*_>~-]/g, "")
    .replace(/\[(.*?)\]\((.*?)\)/g, "$1")
    .replace(/\s+/g, " ")
    .trim();
}

const TIME_FORMAT_OPTIONS = [
  { value: "24", label: "24-hour clock", example: "21:30" },
  { value: "12", label: "12-hour clock", example: "9:30 PM" },
];

function normalizeTimeFormat(value) {
  return String(value || "24") === "12" ? "12" : "24";
}
const DEFAULT_TTS_VOICE = "nova";
const TTS_VOICE_OPTIONS = [
  { value: "nova", label: "Nova - warm and natural" },
  { value: "shimmer", label: "Shimmer - bright and friendly" },
  { value: "coral", label: "Coral - polished and clear" },
  { value: "sage", label: "Sage - calm and measured" },
  { value: "fable", label: "Fable - expressive" },
  { value: "onyx", label: "Onyx - deeper voice" },
  { value: "alloy", label: "Alloy - balanced" },
  { value: "ash", label: "Ash - steady" },
  { value: "ballad", label: "Ballad - softer" },
  { value: "echo", label: "Echo - crisp" },
  { value: "verse", label: "Verse - relaxed" },
];
const TTS_VOICE_VALUES = new Set(TTS_VOICE_OPTIONS.map(option => option.value));

function normalizeTtsVoice(value) {
  const voice = String(value || "").toLowerCase().trim();
  return TTS_VOICE_VALUES.has(voice) ? voice : DEFAULT_TTS_VOICE;
}
function normalizeList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === "string") return value ? [value] : [];
  if (typeof value === "object") return Object.values(value).flat().filter(Boolean);
  return [];
}

function getItemText(item) {
  if (typeof item === "string") return item;
  return item?.title || item?.summary || item?.text || item?.content || item?.name || item?.subject || item?.body || JSON.stringify(item);
}

function getBriefingSection(briefing, keys) {
  for (const key of keys) {
    if (briefing && briefing[key] !== undefined) return normalizeList(briefing[key]);
  }
  return [];
}
function getBriefingSections(briefing) {
  const sections = [
    {
      label: "Calendar",
      description: "What is happening today and what needs planning around.",
      items: getBriefingSection(briefing, ["calendar", "calendar_summary", "events", "schedule"]),
    },
    {
      label: "Tasks",
      description: "Things Northstar is tracking for you.",
      items: getBriefingSection(briefing, ["tasks", "reminders", "task_summary", "open_tasks"]),
    },
    {
      label: "Inbox",
      description: "Unread reminders and assistant notices.",
      items: getBriefingSection(briefing, ["notifications", "unread_notifications", "inbox"]),
    },
  ];

  return sections.filter(section => section.items.length);
}

function getBriefingEmails(briefing) {
  return getBriefingSection(briefing, ["emails", "email", "email_signals", "important_emails", "email_summary"]);
}

function getVisibleReplyEmails(briefing, actionState) {
  return getBriefingEmails(briefing).filter(email => email.needs_reply && !["ignored", "sent"].includes(actionState[email.id]));
}

function splitBriefingSentences(text) {
  const cleaned = String(text || "").replace(/\s+/g, " ").trim();
  if (!cleaned) return [];
  return cleaned
    .match(/[^.!?]+[.!?]+|[^.!?]+$/g)
    ?.map(part => part.trim())
    .filter(Boolean) || [cleaned];
}

function renderBriefingText(text, keyPrefix) {
  const content = String(text || "").trim();
  if (!content) return null;

  if (/\n|^\s*(#{1,3}|[-*]|\d+\.)\s+/m.test(content)) {
    return <div className="ns-briefing-markdown">{renderMarkdown(content)}</div>;
  }

  const sentences = splitBriefingSentences(content);
  if (sentences.length <= 1 || content.length < 150) {
    return <p>{renderInline(content)}</p>;
  }

  const intro = sentences[0];
  const points = sentences.slice(1);
  return (
    <div className="ns-briefing-prose">
      <p>{renderInline(intro)}</p>
      <ul>
        {points.map((sentence, index) => <li key={`${keyPrefix}-sentence-${index}`}>{renderInline(sentence)}</li>)}
      </ul>
    </div>
  );
}

function renderBriefingItems(items, sectionLabel) {
  if (!items.length) return <p>No updates.</p>;

  if (items.length === 1) {
    return renderBriefingText(getItemText(items[0]), `${sectionLabel}-only`);
  }

  return (
    <ul>
      {items.map((item, index) => {
        const text = getItemText(item);
        const sentences = splitBriefingSentences(text);
        const shouldSplit = sentences.length > 1 && text.length > 130;
        return (
          <li key={`${sectionLabel}-${index}`}>
            {shouldSplit ? renderBriefingText(text, `${sectionLabel}-${index}`) : renderInline(text)}
          </li>
        );
      })}
    </ul>
  );
}

function getConnectedAccountServices(acc) {
  const stored = acc.services;
  if (stored !== undefined && stored !== null) {
    const values = Array.isArray(stored) ? stored : String(stored).split(",");
    const selected = values.map(value => String(value).trim().toLowerCase());
    const services = [];
    if (selected.some(value => ["mail", "gmail", "email"].includes(value))) services.push("Email");
    if (selected.includes("calendar")) services.push("Calendar");
    return services;
  }

  const scopeText = String(acc.granted_scopes || acc.scopes || "").toLowerCase();
  const services = [];
  if (scopeText.includes("gmail")) services.push("Email");
  if (scopeText.includes("calendar")) services.push("Calendar");
  return services.length ? services : ["Connected"];
}

function connectedAccountCanCompose(acc) {
  const services = getConnectedAccountServices(acc);
  if (!services.includes("Email")) return true;
  const scopes = String(acc.granted_scopes || acc.scopes || "").toLowerCase();
  return scopes.includes("gmail.compose") || scopes.includes("gmail.modify") || scopes.includes("https://mail.google.com/");
}

function connectedAccountCanWriteCalendar(acc) {
  const services = getConnectedAccountServices(acc);
  if (!services.includes("Calendar")) return true;
  const scopeText = String(acc.granted_scopes || acc.scopes || "").toLowerCase();
  const scopes = new Set(scopeText.match(/https:\/\/www\.googleapis\.com\/auth\/[a-z0-9._-]+/g) || []);
  return scopes.has("https://www.googleapis.com/auth/calendar.events");
}

function App() {
  const [me, setMe] = useState(null);
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState("");
  const [speakReplies, setSpeakReplies] = useState(false);
  const [voiceStyle, setVoiceStyle] = useState(() => normalizeTtsVoice(localStorage.getItem("northstarVoiceStyle")));
  const [editVoiceStyle, setEditVoiceStyle] = useState(() => normalizeTtsVoice(localStorage.getItem("northstarVoiceStyle")));
  const [authMode, setAuthMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [bulkThreadMode, setBulkThreadMode] = useState(false);
  const [selectedThreadIds, setSelectedThreadIds] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [view, setView] = useState("chat"); 
  const [modal, setModal] = useState(null);
  const [settingsSection, setSettingsSection] = useState("general");
  const [sidebarOpen, setSidebarOpen] = useState(false); 
  const [notifications, setNotifications] = useState([]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [voiceAutoPlay, setVoiceAutoPlay] = useState(false);
  const [editVoiceAutoPlay, setEditVoiceAutoPlay] = useState(false);
  const [timeFormat, setTimeFormat] = useState(() => normalizeTimeFormat(localStorage.getItem("northstarTimeFormat")));
  const [editTimeFormat, setEditTimeFormat] = useState(() => normalizeTimeFormat(localStorage.getItem("northstarTimeFormat")));
  const [paBriefing, setPaBriefing] = useState(null);
  const [paDashboard, setPaDashboard] = useState(null);
  const [calendarInsightEditor, setCalendarInsightEditor] = useState(null);
  const [contactCheckIn, setContactCheckIn] = useState(null);
  const [paSettings, setPaSettings] = useState(null);
  const [paTasks, setPaTasks] = useState([]);
  const [paReminders, setPaReminders] = useState([]);
  const [reminderEditor, setReminderEditor] = useState(null);
  const [reminderSaving, setReminderSaving] = useState(false);
  const [paItems, setPaItems] = useState([]);
  const [newPaItem, setNewPaItem] = useState({ kind: "commitment", title: "", due_at: "", priority: "medium" });

  const [paLoading, setPaLoading] = useState(false);
  const [paSettingStatus, setPaSettingStatus] = useState("");
  const [emailReplyDrafts, setEmailReplyDrafts] = useState({});
  const [emailActionState, setEmailActionState] = useState({});
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [ignoredSenders, setIgnoredSenders] = useState([]);
  const [emailSnoozeOpen, setEmailSnoozeOpen] = useState(null);
  const [followUpDrafts, setFollowUpDrafts] = useState({});
  const [followUpState, setFollowUpState] = useState({});
  const [newTaskText, setNewTaskText] = useState("");
  const [updateRequired, setUpdateRequired] = useState(false);
  const [todayLoadError, setTodayLoadError] = useState("");
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [integrationDefaultStatus, setIntegrationDefaultStatus] = useState("");
  const [integrationDefaults, setIntegrationDefaults] = useState({
    default_email_account_id: "",
    default_calendar_record_id: "",
  });
  
  const [renamingThread, setRenamingThread] = useState(null);
  const [imapStatus, setImapStatus] = useState("");
  const [imapSaving, setImapSaving] = useState(false);
  const [imapStatusKind, setImapStatusKind] = useState("info");
  const [imapTestState, setImapTestState] = useState({});
  const [imapForm, setImapForm] = useState({ email: "", username: "", password: "",
    imap_host: "", imap_port: 993, imap_security: "ssl",
    smtp_host: "", smtp_port: 465, smtp_security: "ssl",
    smtp_username: "", folder: "INBOX", drafts_folder: "Drafts" });

  const [deletingThread, setDeletingThread] = useState(null);
  const [newName, setNewName] = useState("");
  
  const [editName, setEditName] = useState("");
  const [editLoc, setEditLoc] = useState("");
  const [editLang, setEditLang] = useState("en");
  const [memories, setMemories] = useState([]);

  const [currentTime, setCurrentTime] = useState("");

  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const voiceTranscriptRef = useRef("");
  const voiceLatestTranscriptRef = useRef("");
  const voiceShouldSendRef = useRef(false);
  const currentAudioRef = useRef(null);
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { scrollToBottom(); }, [messages]);

  const loadSession = useCallback(async () => {
    const user = await apiGet("/auth/me");
    setMe(user);
    setEditName(user.display_name || "");
    setEditLoc(user.location || "");
    setEditLang(user.language || "en");
    const accountTimeFormat = normalizeTimeFormat(user.time_format);
    setTimeFormat(accountTimeFormat);
    setEditTimeFormat(accountTimeFormat);
    localStorage.setItem("northstarTimeFormat", accountTimeFormat);

    try {
      const prefs = await apiGet("/voice/settings");
      const selectedVoice = normalizeTtsVoice(prefs.voice);
      const shouldAutoPlay = prefs.auto_play === true;
      setVoiceStyle(selectedVoice);
      setEditVoiceStyle(selectedVoice);
      setVoiceAutoPlay(shouldAutoPlay);
      setEditVoiceAutoPlay(shouldAutoPlay);
      setSpeakReplies(shouldAutoPlay);
      localStorage.setItem("northstarVoiceStyle", selectedVoice);
    } catch (err) {
      setEditVoiceStyle(voiceStyle);
    }

    const list = await apiGet("/threads");
    setThreads(list);
  }, [voiceStyle]);

  // Initial load
  useEffect(() => {
    unregisterNorthstarPushWorkers();
    loadSession().catch(() => setMe(null));
  }, [loadSession]);

  useEffect(() => {
    const runningBundle = getMainBundleUrl();
    if (!runningBundle) return undefined;

    let checking = false;
    const checkForUpdate = async () => {
      if (checking || document.visibilityState === "hidden") return;
      checking = true;
      try {
        const url = new URL("/", window.location.origin);
        url.searchParams.set("northstar_version_check", Date.now().toString());
        const response = await fetch(url.toString(), {
          cache: "no-store",
          credentials: "same-origin",
          headers: { "Cache-Control": "no-cache" },
        });
        if (!response.ok) return;
        const html = await response.text();
        const latestDocument = new DOMParser().parseFromString(html, "text/html");
        const latestBundle = getMainBundleUrl(latestDocument);
        if (latestBundle && latestBundle !== runningBundle) setUpdateRequired(true);
      } catch (error) {
        // Update checks stay silent if the connection is temporarily unavailable.
      } finally {
        checking = false;
      }
    };

    const onFocus = () => checkForUpdate();
    const onVisibility = () => {
      if (document.visibilityState === "visible") checkForUpdate();
    };
    checkForUpdate();
    const interval = window.setInterval(checkForUpdate, 5 * 60 * 1000);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // TICKING CLOCK LOGIC
  useEffect(() => {
    const updateClock = () => {
      const tz = me?.timezone || "Europe/London";
      const now = new Date().toLocaleTimeString("en-GB", {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
        hour12: timeFormat === "12",
      });
      setCurrentTime(now);
    };

    updateClock();
    const interval = setInterval(updateClock, 60000);
    return () => clearInterval(interval);
  }, [me, timeFormat]);

  useEffect(() => {
    if (modal === 'settings' && me) {
      setEditName(me.display_name || "");
      setEditLoc(me.location || "");
      setEditLang(me.language || "en");
      setEditVoiceStyle(voiceStyle);
      setEditVoiceAutoPlay(voiceAutoPlay);
      setEditTimeFormat(timeFormat);
      apiGet("/integrations/google-calendar/list")
        .then(calendars => {
          const list = Array.isArray(calendars) ? calendars : [];
          setAvailableCalendars(list);
          return apiGet("/integrations/defaults").then(defaults => ({ defaults, list }));
        })
        .then(({ defaults, list }) => {
          const emailAccounts = (me.connected_accounts || []).filter(acc => getConnectedAccountServices(acc).includes("Email"));
          setIntegrationDefaults({
            default_email_account_id: defaults.default_email_account_id || emailAccounts[0]?.id || "",
            default_calendar_record_id: defaults.default_calendar_record_id || list.find(calendar => calendar.is_primary)?.id || list[0]?.id || "",
          });
        })
        .catch(() => {
          setAvailableCalendars([]);
        });
    }
  }, [modal, me, voiceStyle, voiceAutoPlay, timeFormat]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) recognitionRef.current.abort();
      if (currentAudioRef.current) currentAudioRef.current.pause();
      if (window.speechSynthesis) window.speechSynthesis.cancel();
    };
  }, []);

  useEffect(() => {
    if (!("speechSynthesis" in window)) return undefined;
    const warmVoices = () => window.speechSynthesis.getVoices();
    warmVoices();
    window.speechSynthesis.addEventListener?.("voiceschanged", warmVoices);
    return () => window.speechSynthesis.removeEventListener?.("voiceschanged", warmVoices);
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await apiGet("/notifications");
      const list = Array.isArray(res) ? res : (res.notifications || res.items || res.inbox_items || []);
      setNotifications(list);
    } catch (err) {
      // Keep reminders quiet if the endpoint is temporarily unavailable.
    }
  };

  useEffect(() => {
    if (!me) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 45000);
    const onFocus = () => fetchNotifications();
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchNotifications();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  const onLogin = async (e) => {
    e.preventDefault();
    try {
      await apiPost("/auth/login", { email, password });
      await loadSession();
    } catch (err) { alert("Login failed"); }
  };

  const onSignup = async (e) => {
    e.preventDefault();
    try {
      await apiPost("/auth/register", {
        email,
        password,
        display_name: displayName || undefined,
      });
      await loadSession();
    } catch (err) { alert(err.message || "Signup failed"); }
  };

  const startGoogleSignup = async () => {
    try {
      const res = await apiGet("/integrations/google/signup");
      if (res.url) window.location.href = res.url;
    } catch (err) {
      alert("Google sign in is not available right now.");
    }
  };

  const onLogout = async () => {
    await apiPost("/auth/logout");
    setNotificationOpen(false);
    setNotifications([]);
    setMe(null);
  };

  // --- GOOGLE INTEGRATION LOGIC ---
  const connectGoogle = async (services) => {
    try {
      const query = services ? `?services=${encodeURIComponent(services)}` : "";
      const res = await apiGet(`/integrations/google/connect${query}`);
      if (res.url) {
        window.location.href = res.url;
      }
    } catch (err) {
      alert("Failed to initiate Google connection.");
    }
  };

  const connectImap = async (event) => {
    event.preventDefault(); setImapSaving(true); setImapStatusKind("info"); setImapStatus("Testing incoming and outgoing mail...");
    try {
      const result = await apiPost("/integrations/imap/connect", { ...imapForm, imap_port: Number(imapForm.imap_port), smtp_port: Number(imapForm.smtp_port) });
      const updatedUser = await apiGet("/auth/me"); setMe(updatedUser);
      setImapStatusKind("success");
      setImapStatus(`Connection successful. Incoming mail: ${result?.checks?.imap ? "working" : "not confirmed"}. Outgoing mail: ${result?.checks?.smtp ? "working" : "not confirmed"}.`);
      setImapForm(previous => ({ ...previous, password: "" }));
      setIntegrationDefaultStatus(`Email account ${result?.email || imapForm.email} tested and saved securely.`);
      setModal('settings');
    } catch (error) { setImapStatusKind("error"); setImapStatus(error?.message || "Northstar could not connect. Check the server names, ports and app password."); }
    finally { setImapSaving(false); }
  };
  const applyMailPreset = (preset) => {
    const presets = { yahoo: { imap_host: "imap.mail.yahoo.com", imap_port: 993, imap_security: "ssl", smtp_host: "smtp.mail.yahoo.com", smtp_port: 465, smtp_security: "ssl" },
      icloud: { imap_host: "imap.mail.me.com", imap_port: 993, imap_security: "ssl", smtp_host: "smtp.mail.me.com", smtp_port: 587, smtp_security: "starttls" } };
    setImapForm(previous => ({ ...previous, ...(presets[preset] || {}) }));
  };
  const testImapAccount = async (account) => {
    setImapTestState(previous => ({ ...previous, [account.id]: { state: "testing", message: "Testing incoming and outgoing mail..." } }));
    try {
      const result = await apiPost(`/integrations/imap/test/${account.id}`);
      const message = `Working — incoming ${result?.checks?.imap ? "connected" : "not confirmed"}, outgoing ${result?.checks?.smtp ? "connected" : "not confirmed"}.`;
      setImapTestState(previous => ({ ...previous, [account.id]: { state: "success", message } }));
    } catch (error) {
      setImapTestState(previous => ({ ...previous, [account.id]: { state: "error", message: error?.message || "Connection test failed." } }));
    }
  };


  const disconnectAccount = async (accountId, provider = "google") => {
    const approved = await requestConfirmation({
      title: "Disconnect this account?",
      message: "Northstar will lose access to the calendars and email connected through this account.",
      confirmLabel: "Disconnect",
      tone: "danger",
    });
    if (!approved) return;
    try {
        await apiDelete(provider === "imap" ? `/integrations/imap/accounts/${accountId}` : `/integrations/accounts/${accountId}`);
        // Refresh local user state
        const updatedUser = await apiGet("/auth/me");
        setMe(updatedUser);
    } catch (err) {
        alert("Failed to disconnect account.");
    }
  };

  const createThread = async () => {
    const t = await apiPost("/threads");
    setThreads([t, ...threads]);
    setActiveThreadId(t.id);
    setMessages([]);
    setView("chat");
    setSidebarOpen(false);
  };

  const toggleThreadSelection = (id) => {
    const key = String(id);
    setSelectedThreadIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearBulkThreadSelection = () => {
    setBulkThreadMode(false);
    setSelectedThreadIds(new Set());
    setBulkDeleteOpen(false);
  };

  const selectThread = async (id) => {
    if (bulkThreadMode) {
      const thread = threads.find(item => String(item.id) === String(id));
      if (thread?.is_locked) return;
      toggleThreadSelection(id);
      return;
    }
    setActiveThreadId(id);
    setMenuOpenFor(null);
    const msgs = await apiGet(`/threads/${id}/messages`);
    setMessages(msgs);
    setView("chat");
    setSidebarOpen(false);
  };

  const getPreferredSpeechVoice = (style = voiceStyle) => {
    if (!("speechSynthesis" in window)) return null;
    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) return null;
    const language = me?.language === "en" ? "en-GB" : (me?.language || "en-GB");
    const englishVoices = voices.filter(voice => voice.lang === language || voice.lang?.startsWith("en"));
    const voicePool = englishVoices.length ? englishVoices : voices;
    if (style === "device") return voicePool[0] || voices[0];

    const styleNames = {
      female: ["Serena", "Samantha", "Kate", "Microsoft Sonia", "Google UK English Female", "Karen", "Moira", "Tessa", "Fiona"],
      male: ["Daniel", "Oliver", "Jamie", "Microsoft Ryan", "Google UK English Male", "Arthur", "Fred"],
      natural: ["Serena", "Samantha", "Daniel", "Kate", "Oliver", "Jamie", "Microsoft Sonia", "Microsoft Ryan", "Microsoft Libby", "Google UK English Female", "Google UK English Male", "Karen", "Moira", "Tessa", "Fiona"]
    };

    const naturalVoice = voicePool.find(voice => /natural|premium|enhanced/i.test(voice.name));
    if (style === "natural" && naturalVoice) return naturalVoice;

    const namedVoice = (styleNames[style] || styleNames.natural)
      .map(name => voicePool.find(voice => voice.name.toLowerCase().includes(name.toLowerCase())))
      .find(Boolean);
    if (namedVoice) return namedVoice;
    if (naturalVoice) return naturalVoice;
    return voicePool.find(voice => voice.lang === language) || voicePool[0] || voices[0];
  };

  const stopSpeech = () => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();
  };

  const getSpeechInstructions = () => (
    "Speak as Northstar, a calm British personal assistant. Sound warm, conversational, and natural. Use gentle pacing and natural pauses. Avoid robotic, announcer, or sales-presenter delivery."
  );

  const playGeneratedSpeech = async (content, voiceOverride = voiceStyle) => {
    const spoken = stripMarkdownForSpeech(content);
    if (!spoken) return;
    stopSpeech();
    const blob = await apiPostBlob("/voice/tts", {
      text: spoken,
      voice: normalizeTtsVoice(voiceOverride),
      format: "mp3",
      instructions: getSpeechInstructions(),
    });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    currentAudioRef.current = audio;
    audio.onended = () => {
      URL.revokeObjectURL(url);
      if (currentAudioRef.current === audio) currentAudioRef.current = null;
    };
    audio.onerror = () => URL.revokeObjectURL(url);
    await audio.play();
  };

  const unlockSpeechSynthesis = () => {
    if (!window.speechSynthesis) return;
    try {
      window.speechSynthesis.resume();
      window.speechSynthesis.getVoices();
    } catch (err) {
      // Some browsers block speech until the first user gesture.
    }
  };

  const speakWithDeviceVoice = (content, styleOverride = voiceStyle) => {
    if (!("speechSynthesis" in window)) return;
    const spoken = stripMarkdownForSpeech(content);
    if (!spoken) return;
    stopSpeech();
    window.speechSynthesis.resume();
    const utterance = new SpeechSynthesisUtterance(spoken);
    const voice = getPreferredSpeechVoice(styleOverride);
    if (voice) {
      utterance.voice = voice;
      utterance.lang = voice.lang;
    } else {
      utterance.lang = me?.language === "en" ? "en-GB" : (me?.language || "en-GB");
    }
    utterance.rate = 0.92;
    utterance.pitch = 0.98;
    utterance.volume = 1;
    window.speechSynthesis.speak(utterance);
  };

  const speakAssistantReply = async (content, force = false, styleOverride = voiceStyle) => {
    if (!force && !speakReplies) return;
    try {
      await playGeneratedSpeech(content, styleOverride);
    } catch (err) {
      speakWithDeviceVoice(content, styleOverride);
    }
  };

  const playVoiceSample = () => {
    speakAssistantReply("Hello, I'm Northstar. This is my generated voice for spoken replies.", true, editVoiceStyle);
  };

  const sendMessageText = async (content, options = {}) => {
    const text = String(content || "").trim();
    if (!text || !activeThreadId || loading) return;
    const userMsg = { role: "user", content: text };
    setMessages(prev => [...prev, userMsg]);
    setDraft("");
    setVoiceStatus("");
    setLoading(true);
    try {
      const res = await apiPost("/message", { thread_id: activeThreadId, content: userMsg.content });
      setMessages(prev => [...prev, { role: "assistant", content: res.content }]);
      if (speakReplies || options.speakReply) speakAssistantReply(res.content);
      const responseThreadId = res.thread_id || res.thread?.id || activeThreadId;
      const responseTitle = res.thread_title || res.thread?.title || res.title;
      if (responseThreadId && responseTitle) {
        setThreads(prev => {
          const matchesThread = (thread) => String(thread.id) === String(responseThreadId);
          const updated = prev.map(t => (
            matchesThread(t) ? { ...t, title: responseTitle } : t
          ));
          const thread = updated.find(matchesThread);
          if (!thread) return updated;
          return [thread, ...updated.filter(t => !matchesThread(t))];
        });
      }
    } catch (err) { alert("Failed to send message."); }
    finally { setLoading(false); }
  };

  const onSendMessage = async (e) => {
    if (e) e.preventDefault();
    if (speakReplies) unlockSpeechSynthesis();
    await sendMessageText(draft);
  };

  const startVoiceMessage = () => {
    if (loading || isListening) return;
    if (speakReplies) unlockSpeechSynthesis();
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setVoiceStatus("Voice input is not available in this browser. Try Safari on iOS 14.5+ or Chrome.");
      return;
    }

    stopSpeech();
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    voiceTranscriptRef.current = "";
    voiceLatestTranscriptRef.current = "";
    voiceShouldSendRef.current = true;

    recognition.lang = me?.language === "en" ? "en-GB" : (me?.language || "en-GB");
    recognition.interimResults = true;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setVoiceStatus("Listening...");
      setDraft("");
    };

    recognition.onresult = (event) => {
      let interim = "";
      let finalText = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalText += text;
        else interim += text;
      }
      if (finalText.trim()) voiceTranscriptRef.current = `${voiceTranscriptRef.current} ${finalText}`.trim();
      const visibleTranscript = `${voiceTranscriptRef.current} ${interim}`.trim();
      voiceLatestTranscriptRef.current = visibleTranscript;
      setDraft(visibleTranscript);
    };

    recognition.onerror = (event) => {
      voiceShouldSendRef.current = false;
      setIsListening(false);
      if (event.error === "not-allowed") setVoiceStatus("Microphone permission was blocked.");
      else if (event.error === "no-speech") setVoiceStatus("I did not catch anything. Tap Mic and try again.");
      else setVoiceStatus("Voice input stopped. Please try again.");
    };

    recognition.onend = () => {
      const spoken = (voiceTranscriptRef.current || voiceLatestTranscriptRef.current).trim();
      setIsListening(false);
      recognitionRef.current = null;
      if (voiceShouldSendRef.current && spoken) {
        setVoiceStatus("Sending...");
        sendMessageText(spoken, { speakReply: true });
      } else if (!spoken && voiceShouldSendRef.current) {
        setVoiceStatus("I did not catch anything. Tap Mic and try again.");
      }
      voiceShouldSendRef.current = false;
    };

    try {
      recognition.start();
    } catch (err) {
      setIsListening(false);
      setVoiceStatus("Voice input could not start. Please try again.");
    }
  };

  const stopVoiceMessage = () => {
    if (!recognitionRef.current) return;
    setVoiceStatus("Finishing...");
    recognitionRef.current.stop();
  };

  const handleVoiceButton = () => {
    if (isListening) stopVoiceMessage();
    else startVoiceMessage();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (speakReplies) unlockSpeechSynthesis();
      sendMessageText(draft);
    }
  };

  const toggleLock = async (t) => {
    const newLockStatus = !t.is_locked;
    try {
      await apiPatch(`/threads/${t.id}`, { is_locked: newLockStatus });
      setThreads(threads.map(item => item.id === t.id ? { ...item, is_locked: newLockStatus } : item));
    } catch (err) { alert("Lock toggle failed"); }
    setMenuOpenFor(null);
  };

  const handleRename = async () => {
    if (!newName.trim() || !renamingThread) return;
    try {
      await apiPatch(`/threads/${renamingThread.id}`, { title: newName });
      setThreads(threads.map(t => t.id === renamingThread.id ? { ...t, title: newName } : t));
      setRenamingThread(null);
      setNewName("");
    } catch (err) { alert("Rename failed"); }
  };

  const confirmDelete = async () => {
    if (!deletingThread || deletingThread.is_locked) return;
    try {
      await apiDelete(`/threads/${deletingThread.id}`);
      setThreads(threads.filter(t => t.id !== deletingThread.id));
      setSelectedThreadIds(prev => {
        const next = new Set(prev);
        next.delete(String(deletingThread.id));
        return next;
      });
      if (activeThreadId === deletingThread.id) {
        setActiveThreadId(null);
        setMessages([]);
      }
      setDeletingThread(null);
    } catch (err) { alert("Delete failed"); }
  };

  const selectedThreads = threads.filter(t => selectedThreadIds.has(String(t.id)));
  const selectedDeletableThreads = selectedThreads.filter(t => !t.is_locked);
  const selectedLockedCount = selectedThreads.length - selectedDeletableThreads.length;

  const confirmBulkDelete = async () => {
    if (!selectedDeletableThreads.length) return;
    setBulkDeleting(true);
    try {
      const idsToDelete = selectedDeletableThreads.map(t => t.id);
      await Promise.all(idsToDelete.map(id => apiDelete(`/threads/${id}`)));
      const deletedIds = new Set(idsToDelete.map(String));
      setThreads(prev => prev.filter(t => !deletedIds.has(String(t.id))));
      if (deletedIds.has(String(activeThreadId))) {
        setActiveThreadId(null);
        setMessages([]);
      }
      clearBulkThreadSelection();
    } catch (err) {
      alert("Could not delete selected threads.");
    } finally {
      setBulkDeleting(false);
    }
  };

  const normalizeBriefingTime = (value) => {
    const raw = String(value || "07:30");
    const match = raw.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return "07:30";
    return `${match[1].padStart(2, "0")}:${match[2]}`;
  };

  const normalizePaSettings = (settings) => {
    if (!settings) return settings;
    return {
      ...settings,
      briefing_time: normalizeBriefingTime(settings.briefing_time || settings.daily_briefing_time || settings.briefingTime),
    };
  };

  const loadTodayData = async () => {
    setTodayLoadError("");
    setPaLoading(true);
    const requests = [
      ["PA dashboard", apiGet("/pa/dashboard")],
      ["settings", apiGet("/pa/settings")],
      ["tasks", apiGet("/pa/tasks")],
      ["reminders", apiGet("/pa/reminders")],
      ["ignored senders", apiGet("/pa/email/ignored")],
      ["PA desk", apiGet("/pa/items")],
    ];
    try {
      const results = await Promise.allSettled(requests.map(([, request]) => request));
      const failures = results
        .map((result, index) => result.status === "rejected" ? requests[index][0] : null)
        .filter(Boolean);

      const [briefingResult, settingsResult, tasksResult, remindersResult, ignoredResult, paItemsResult] = results;
      if (briefingResult.status === "fulfilled") {
        const dashboard = briefingResult.value;
        const briefing = dashboard?.briefing || dashboard;
        setPaDashboard(dashboard?.briefing ? dashboard : null);
        setPaBriefing(briefing);
        setFollowUpDrafts(previous => {
          const next = { ...previous };
          (briefing.waiting_for_replies || []).forEach(item => { next[item.id] = previous[item.id] ?? item.suggested_follow_up ?? ""; });
          return next;
        });
        setEmailReplyDrafts(previous => {
          const next = {};
          getBriefingEmails(briefing).forEach(email => {
            next[email.id] = previous[email.id] ?? email.suggested_reply ?? "";
          });
          return next;
        });
      }
      if (settingsResult.status === "fulfilled") {
        setPaSettings(normalizePaSettings(settingsResult.value));
      }
      if (tasksResult.status === "fulfilled") {
        const tasks = tasksResult.value;
        setPaTasks(Array.isArray(tasks) ? tasks : (tasks?.tasks || tasks?.items || []));
      }
      if (remindersResult.status === "fulfilled") {
        setPaReminders(Array.isArray(remindersResult.value) ? remindersResult.value : []);
      }
      if (ignoredResult.status === "fulfilled") {
        setIgnoredSenders(Array.isArray(ignoredResult.value) ? ignoredResult.value : []);
      }
      if (paItemsResult.status === "fulfilled") {
        setPaItems(Array.isArray(paItemsResult.value) ? paItemsResult.value : []);
      }
      if (failures.length) {
        setTodayLoadError(`Some Today information is temporarily unavailable: ${failures.join(", ")}.`);
      }
      await fetchNotifications();
    } catch (err) {
      setTodayLoadError("Some Today information is temporarily unavailable.");
    } finally {
      setPaLoading(false);
    }
  };

  const loadTodayView = async () => {
    setView("today");
    setSidebarOpen(false);
    await loadTodayData();
  };

  const requestConfirmation = ({ title, message, confirmLabel, tone = "primary" }) => (
    new Promise(resolve => setConfirmDialog({ title, message, confirmLabel, tone, resolve }))
  );

  const closeConfirmation = (approved) => {
    const resolve = confirmDialog?.resolve;
    setConfirmDialog(null);
    resolve?.(approved);
  };

  const handleEmailReplyAction = async (email, action) => {
    const body = String(emailReplyDrafts[email.id] || "").trim();
    if (!body) {
      alert("Write or amend the reply before continuing.");
      return;
    }
    if (action === "send") {
      const approved = await requestConfirmation({
        title: "Send this reply?",
        message: `This will send your reply to ${email.from}. Northstar cannot undo it after sending.`,
        confirmLabel: "Send reply",
        tone: "primary",
      });
      if (!approved) return;
    }
    setEmailActionState(previous => ({ ...previous, [email.id]: action === "send" ? "sending" : "saving" }));
    try {
      const result = await apiPost("/pa/email/reply", {
        account: email.account,
        message_id: email.id,
        body,
        action,
        suggested_reply: email.suggested_reply || "",
      });
      setEmailActionState(previous => ({
        ...previous,
        [email.id]: result.action === "sent" ? "sent" : "drafted",
      }));
    } catch (err) {
      setEmailActionState(previous => ({ ...previous, [email.id]: "error" }));
      const message = String(err?.message || err);
      if (message.includes("Reconnect")) {
        alert("Please reconnect Google in Settings to allow Northstar to create and send Gmail drafts.");
      } else {
        alert("Northstar could not complete that email action. Your reply has not been sent.");
      }
    }
  };

  const handleEmailIgnore = async (email) => {
    const approved = await requestConfirmation({
      title: "Ignore this sender?",
      message: `Northstar will stop suggesting replies to future emails from ${email.from}. Nothing will be changed in Gmail.`,
      confirmLabel: "Ignore sender",
      tone: "danger",
    });
    if (!approved) return;
    setEmailActionState(previous => ({ ...previous, [email.id]: "ignoring" }));
    try {
      await apiPost("/pa/email/ignore", {
        account: email.account,
        message_id: email.id,
      });
      setEmailActionState(previous => ({ ...previous, [email.id]: "ignored" }));
    } catch (err) {
      setEmailActionState(previous => ({ ...previous, [email.id]: "error" }));
      alert("Northstar could not save that ignore preference. The email has not been hidden.");
    }
  };
  const handleEmailSnooze = async (email, days) => {
    const until = new Date();
    until.setDate(until.getDate() + days);
    until.setHours(9, 0, 0, 0);
    setEmailActionState(previous => ({ ...previous, [email.id]: "snoozing" }));
    try {
      await apiPost("/pa/email/snooze", {
        account: email.account,
        message_id: email.id,
        until: until.toISOString(),
      });
      setEmailActionState(previous => ({ ...previous, [email.id]: "ignored" }));
    } catch (err) {
      setEmailActionState(previous => ({ ...previous, [email.id]: "error" }));
      alert("Northstar could not snooze that email.");
    }
  };

  const restoreIgnoredSender = async (rule) => {
    try {
      await apiDelete(`/pa/email/ignored/${rule.id}`);
      setIgnoredSenders(previous => previous.filter(item => item.id !== rule.id));
    } catch (err) {
      alert("Northstar could not restore that sender.");
    }
  };


  const handlePriorityAction = async (item, action) => {
    if (action === "complete") {
      await completePaTask(paTasks.find(task => task.id === item.source_id) || { id: item.source_id, title: item.title });
      await loadTodayData();
      return;
    }
    if (action === "tomorrow" || action === "next_week") {
      const reminder = paReminders.find(entry => entry.id === item.source_id) || { id: item.source_id, title: item.title };
      await postponeReminder(reminder, action === "tomorrow" ? 1440 : 10080);
      await loadTodayData();
      return;
    }
    if (action === "review_reply") {
      const target = document.getElementById(`reply-${item.email?.id}`);
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      window.setTimeout(() => target?.focus(), 350);
      return;
    }
    if (action === "snooze") {
      await handleEmailSnooze(item.email, 1);
      await loadTodayData();
      return;
    }
    if (action === "ignore") {
      await handleEmailIgnore(item.email);
      await loadTodayData();
      return;
    }
    if (action === "directions") {
      const location = item.event?.location;
      if (location) window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(location)}`, "_blank", "noopener,noreferrer");
      return;
    }
    if (action === "check_in") {
      openContactCheckIn(item.contact);
      return;
    }
    if (action === "follow_up") {
      setDraft(`Draft a polite follow-up about "${item.title}" to ${item.waiting?.recipient || "the recipient"}.`);
      setView("chat");
    }
  };

  const handleCalendarInsight = async (insight) => {
    const events = (insight.events || [insight.event]).filter(Boolean);
    const eventNames = events.map(event => event.title).join(" and ");
    if (insight.kind === "missing_location" || insight.kind === "clash" || insight.kind === "travel_gap") {
      const event = events[0];
      setCalendarInsightEditor({
        insightId: insight.id,
        kind: insight.kind,
        events,
        eventId: event?.id || "",
        location: event?.location || "",
        startAt: event?.start_at?.slice(0, 16) || "",
        saving: false,
        error: "",
      });
      return;
    }
    const instruction = `Help me prepare for "${eventNames}" and add preparation time to my calendar if useful.`;
    setView("chat");
    if (activeThreadId) {
      await sendMessageText(instruction);
    } else {
      setDraft(instruction);
    }
  };

  const saveCalendarInsight = async () => {
    const editor = calendarInsightEditor;
    const event = editor?.events?.find(item => String(item.id) === String(editor.eventId));
    if (!editor || !event) return;
    if (editor.kind === "missing_location" && !String(editor.location || "").trim()) {
      setCalendarInsightEditor(previous => ({ ...previous, error: "Enter a location first." }));
      return;
    }
    if (editor.kind !== "missing_location" && !editor.startAt) {
      setCalendarInsightEditor(previous => ({ ...previous, error: "Choose a new date and time." }));
      return;
    }
    const approved = await requestConfirmation({
      title: editor.kind === "missing_location" ? "Update event location?" : "Move this event?",
      message: editor.kind === "missing_location"
        ? `Set the location for "${event.title}" to "${editor.location.trim()}"?`
        : `Move "${event.title}" to the selected date and time?`,
      confirmLabel: editor.kind === "missing_location" ? "Save location" : "Move event",
    });
    if (!approved) return;
    setCalendarInsightEditor(previous => ({ ...previous, saving: true, error: "" }));
    try {
      await apiPatch("/pa/calendar/event", {
        account: event.account,
        calendar_id: event.calendar_id,
        event_id: event.id,
        ...(editor.kind === "missing_location"
          ? { location: editor.location.trim() }
          : { start_at: new Date(editor.startAt).toISOString() }),
      });
      setCalendarInsightEditor(null);
      await loadTodayData();
    } catch (error) {
      setCalendarInsightEditor(previous => ({ ...previous, saving: false, error: error.message || "Could not update this event." }));
    }
  };

  const ignoreCalendarInsight = async (insight) => {
    setPaDashboard(previous => ({
      ...previous,
      calendar_intelligence: (previous?.calendar_intelligence || []).filter(item => item.id !== insight.id),
    }));
    try {
      await apiPost("/pa/calendar-insights/ignore", { insight_id: insight.id });
    } catch (error) {
      await loadTodayData();
    }
  };

  const handleFollowUp = async (item, action) => {
    const body = String(followUpDrafts[item.id] || "").trim();
    if (!body) return;
    if (action === "send") {
      const approved = await requestConfirmation({
        title: "Send this follow-up?",
        message: `This will email ${item.recipient}. It cannot be undone after sending.`,
        confirmLabel: "Send follow-up",
      });
      if (!approved) return;
    }
    setFollowUpState(previous => ({ ...previous, [item.id]: action === "send" ? "sending" : "saving" }));
    try {
      const result = await apiPost("/pa/email/follow-up", { state_id: item.id, body, action });
      setFollowUpState(previous => ({ ...previous, [item.id]: result.action === "sent" ? "sent" : "drafted" }));
      if (result.action === "sent") window.setTimeout(loadTodayData, 500);
    } catch (error) {
      setFollowUpState(previous => ({ ...previous, [item.id]: "error" }));
    }
  };

  const updatePaSetting = async (key, value) => {
    const normalizedValue = key === "briefing_time" ? normalizeBriefingTime(value) : value;
    const next = normalizePaSettings({ ...(paSettings || {}), [key]: normalizedValue });
    const apiKey = key === "briefing_time" ? "daily_briefing_time" : key;
    setPaSettings(next);
    setPaSettingStatus("Saving...");
    try {
      const updated = await apiPatch("/pa/settings", { [apiKey]: normalizedValue });
      setPaSettings(normalizePaSettings({ ...next, ...(updated || {}) }));
      setPaSettingStatus("Saved");
    } catch (err) {
      setPaSettingStatus("Could not save");
      alert("Could not save briefing setting.");
      loadTodayData();
    }
  };

  const openReminderEditor = (reminder) => {
    setReminderEditor({
      id: reminder.id,
      title: reminder.title || "",
      remind_at: toDateTimeLocalValue(reminder.next_run_at || reminder.due_at),
      repeat: reminder.repeat || "once",
    });
  };

  const saveReminder = async (event) => {
    event.preventDefault();
    if (!reminderEditor?.title.trim() || !reminderEditor?.remind_at) return;
    setReminderSaving(true);
    try {
      const updated = await apiPatch(`/pa/reminders/${reminderEditor.id}`, {
        title: reminderEditor.title.trim(),
        remind_at: reminderEditor.remind_at,
        repeat: reminderEditor.repeat,
      });
      setPaReminders(previous => previous.map(item => item.id === updated.id ? updated : item)
        .sort((a, b) => new Date(a.next_run_at || a.due_at) - new Date(b.next_run_at || b.due_at)));
      setReminderEditor(null);
    } catch (error) {
      setTodayLoadError("Northstar could not update that reminder.");
    } finally {
      setReminderSaving(false);
    }
  };

  const postponeReminder = async (reminder, minutes) => {
    try {
      const updated = await apiPost(`/pa/reminders/${reminder.id}/snooze`, { minutes });
      setPaReminders(previous => previous.map(item => item.id === updated.id ? updated : item)
        .sort((a, b) => new Date(a.next_run_at || a.due_at) - new Date(b.next_run_at || b.due_at)));
    } catch (error) {
      setTodayLoadError("Northstar could not postpone that reminder.");
    }
  };

  const cancelReminder = async (reminder) => {
    const confirmed = await requestConfirmation({
      title: "Cancel this reminder?",
      message: reminder.title,
      confirmLabel: "Cancel reminder",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await apiDelete(`/pa/reminders/${reminder.id}`);
      setPaReminders(previous => previous.filter(item => item.id !== reminder.id));
    } catch (error) {
      setTodayLoadError("Northstar could not cancel that reminder.");
    }
  };
  const createPaTask = async (e) => {
    if (e) e.preventDefault();
    const text = newTaskText.trim();
    if (!text) return;
    try {
      const created = await apiPost("/pa/tasks", { title: text, text });
      setPaTasks(prev => [created, ...prev]);
      setNewTaskText("");
    } catch (err) {
      alert("Could not add task.");
    }
  };

  const completePaTask = async (task) => {
    try {
      await apiPost(`/pa/tasks/${task.id}/complete`);
      setPaTasks(prev => prev.map(item => (
        item.id === task.id ? { ...item, completed: true, completed_at: new Date().toISOString() } : item
      )));
    } catch (err) {
      alert("Could not complete task.");
    }
  };

  const openContactCheckIn = (contact) => {
    setContactCheckIn({ id: contact.id, name: contact.name, notes: "", next_contact_at: "" });
  };

  const saveContactCheckIn = async (event) => {
    event.preventDefault();
    if (!contactCheckIn) return;
    try {
      const relationships = await apiPost(`/pa/relationships/${contactCheckIn.id}/check-in`, {
        notes: contactCheckIn.notes || null,
        next_contact_at: contactCheckIn.next_contact_at || null,
      });
      setPaDashboard(previous => previous ? { ...previous, relationships } : previous);
      setContactCheckIn(null);
    } catch (error) {
      setTodayLoadError("Northstar could not record that check-in.");
    }
  };
  const createPaItem = async (event) => {
    event.preventDefault();
    if (!newPaItem.title.trim()) return;
    try {
      const created = await apiPost("/pa/items", {
        ...newPaItem,
        title: newPaItem.title.trim(),
        due_at: newPaItem.due_at || null,
        requires_approval: newPaItem.kind === "approval",
      });
      setPaItems(previous => [created, ...previous]);
      setNewPaItem({ kind: "commitment", title: "", due_at: "", priority: "medium" });
    } catch (error) {
      alert("Northstar could not add that PA item.");
    }
  };

  const resolvePaItem = async (item, action) => {
    if (action === "approve") {
      const approved = await requestConfirmation({ title: "Approve this action?", message: item.proposed_action || item.title, confirmLabel: "Approve" });
      if (!approved) return;
    }
    try {
      await apiPost(`/pa/items/${item.id}/${action}`);
      setPaItems(previous => previous.filter(candidate => candidate.id !== item.id));
    } catch (error) {
      alert("Northstar could not update that PA item.");
    }
  };
  const loadMemories = async () => {
    const list = await apiGet("/user/memories");
    setMemories(list);
    setView("memory");
    setSidebarOpen(false);
  };

  const deleteMem = async (id) => {
    try {
      await apiDelete(`/user/memories/${id}`);
      setMemories(memories.filter(m => m.id !== id));
    } catch (err) { alert("Failed to forget memory"); }
  };

  const toggleGoogleCalendar = async (account, enabled) => {
    setIntegrationDefaultStatus(enabled ? "Enabling calendar..." : "Disabling calendar...");
    try {
      const updated = await apiPatch(`/integrations/accounts/${account.id}/services`, { calendar_enabled: enabled });
      setMe(previous => ({
        ...previous,
        connected_accounts: (previous.connected_accounts || []).map(item => item.id === account.id ? { ...item, ...updated } : item),
      }));
      const calendars = await apiGet("/integrations/google-calendar/list");
      const list = Array.isArray(calendars) ? calendars : [];
      setAvailableCalendars(list);
      const defaults = await apiGet("/integrations/defaults");
      setIntegrationDefaults(previous => ({
        ...previous,
        default_calendar_record_id: defaults.default_calendar_record_id || list.find(calendar => calendar.is_primary)?.id || list[0]?.id || "",
      }));
      setIntegrationDefaultStatus(enabled ? "Calendar enabled" : "Calendar disabled");
    } catch (error) {
      const message = String(error?.message || error);
      setIntegrationDefaultStatus("Could not update calendar");
      if (enabled && message.includes("Reconnect")) {
        const approved = await requestConfirmation({
          title: "Calendar permission needed",
          message: "Google needs to approve Calendar access for this account. Reconnect it now?",
          confirmLabel: "Reconnect Google",
        });
        if (approved) connectGoogle("calendar,mail");
      }
    }
  };

  const selectIntegrationDefault = async (field, value) => {
    setIntegrationDefaults(previous => ({ ...previous, [field]: value }));
    if (!value) return;
    setIntegrationDefaultStatus("Saving...");
    try {
      await apiPatch("/integrations/defaults", { [field]: Number(value) });
      setIntegrationDefaultStatus("Default saved");
    } catch (error) {
      setIntegrationDefaultStatus("Could not save default");
    }
  };

  const saveSettings = async () => {
    try {
        const selectedVoice = normalizeTtsVoice(editVoiceStyle);
        localStorage.setItem("northstarVoiceStyle", selectedVoice);
        setVoiceStyle(selectedVoice);
        setVoiceAutoPlay(editVoiceAutoPlay);
        setSpeakReplies(editVoiceAutoPlay);
        setTimeFormat(editTimeFormat);
        localStorage.setItem("northstarTimeFormat", editTimeFormat);
        await apiPatch("/voice/settings", {
            voice: selectedVoice,
            auto_play: editVoiceAutoPlay,
        });
        const updated = await apiPatch("/user/me", { 
            display_name: editName, 
            location: editLoc,
            language: editLang,
            time_format: editTimeFormat,
        });
        await apiPatch("/integrations/defaults", {
            default_email_account_id: integrationDefaults.default_email_account_id ? Number(integrationDefaults.default_email_account_id) : null,
            default_calendar_record_id: integrationDefaults.default_calendar_record_id ? Number(integrationDefaults.default_calendar_record_id) : null,
        });
        setMe(previous => ({ ...previous, ...updated }));
        setModal(null);
    } catch (err) { 
        console.error(err);
        alert("Save failed. Check console for details."); 
    }
  };

  const unreadNotifications = notifications.filter(isNotificationUnread);

  const markNotificationRead = async (notification) => {
    if (!notification?.id) return;
    setNotifications(prev => prev.map(item => (
      item.id === notification.id ? { ...item, is_read: true, read_at: new Date().toISOString() } : item
    )));
    try {
      await apiPost(`/notifications/${notification.id}/read`);
    } catch (err) {
      fetchNotifications();
    }
  };
  const clearAllNotifications = async () => {
    if (!unreadNotifications.length) return;
    const readAt = new Date().toISOString();
    setNotifications(prev => prev.map(item => (
      isNotificationUnread(item) ? { ...item, is_read: true, read_at: readAt } : item
    )));
    try {
      await apiPost("/notifications/read-all");
      await fetchNotifications();
    } catch (err) {
      fetchNotifications();
    }
  };

  const openNotification = async (notification) => {
    await markNotificationRead(notification);
    setNotificationOpen(false);
    const target = notification.url || notification.link;
    if (target) window.location.href = target;
  };

  if (!me) {
    return (
      <div className="ns-auth">
        <form className="ns-auth-card" onSubmit={authMode === "login" ? onLogin : onSignup}>
          <div className="ns-auth-brand">
            <h1>Northstar PA</h1>
            <p>Your private AI personal assistant.</p>
          </div>
          <div className="ns-auth-tabs" role="tablist" aria-label="Authentication mode">
            <button
              type="button"
              className={authMode === "login" ? "active" : ""}
              onClick={() => setAuthMode("login")}
            >
              Sign in
            </button>
            <button
              type="button"
              className={authMode === "signup" ? "active" : ""}
              onClick={() => setAuthMode("signup")}
            >
              Create account
            </button>
          </div>
          {authMode === "signup" && (
            <input type="text" placeholder="Name (optional)" value={displayName} onChange={e => setDisplayName(e.target.value)} />
          )}
          <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required />
          <input type="password" placeholder={authMode === "signup" ? "Password (minimum 8 characters)" : "Password"} value={password} onChange={e => setPassword(e.target.value)} minLength={authMode === "signup" ? 8 : undefined} required />
          <button className="ns-btn primary full" type="submit">{authMode === "login" ? "Sign in" : "Create account"}</button>
          <div className="ns-auth-divider"><span>or</span></div>
          <button className="ns-btn google full" type="button" onClick={startGoogleSignup}>
            Continue with Google
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="ns-app">
      <header className="ns-header">
        <div className="ns-header-left">
          <button className="ns-burger-btn" onClick={() => setSidebarOpen(true)}>
            <div className="ns-burger-line"></div>
            <div className="ns-burger-line"></div>
            <div className="ns-burger-line"></div>
          </button>
          <div className="ns-logo" onClick={() => setView("chat")} style={{cursor:'pointer'}}>Northstar PA</div>
        </div>
        <div className="ns-user-nav">
          <span className="ns-clock-display" style={{marginRight: '15px', fontWeight: '600', color: '#4a5568'}}>
            {currentTime}
          </span>
          <div className="ns-notification-wrap">
            <button
              type="button"
              className={`ns-inbox-btn ${unreadNotifications.length ? "has-unread" : ""}`}
              onClick={() => setNotificationOpen(open => !open)}
              aria-label={`${unreadNotifications.length} unread notifications`}
            >
              Inbox
              {unreadNotifications.length > 0 && <span>{unreadNotifications.length}</span>}
            </button>
            {notificationOpen && (
              <div className="ns-notification-panel">
                <div className="ns-notification-head">
                  <div>
                    <strong>Inbox</strong>
                    <span>{unreadNotifications.length} unread</span>
                  </div>
                  <div className="ns-notification-actions">
                    {unreadNotifications.length > 0 && (
                      <button type="button" onClick={clearAllNotifications}>Clear all</button>
                    )}
                    <button type="button" onClick={() => setNotificationOpen(false)}>Close</button>
                  </div>
                </div>
                {unreadNotifications.length > 0 ? (
                  <div className="ns-notification-list">
                    {unreadNotifications.map(notification => (
                      <div key={notification.id} className="ns-notification-item">
                        <button type="button" className="ns-notification-content" onClick={() => openNotification(notification)}>
                          <strong>{getNotificationTitle(notification)}</strong>
                          {getNotificationBody(notification) && (isDailyBriefingNotification(notification) ? <BriefingNotificationBody notification={notification} /> : <span>{getNotificationBody(notification)}</span>)}
                          {formatNotificationTime(notification, timeFormat) && <small>{formatNotificationTime(notification, timeFormat)}</small>}
                        </button>
                        <button type="button" className="ns-notification-dismiss" onClick={() => markNotificationRead(notification)}>
                          Dismiss
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="ns-notification-empty">No unread reminders.</div>
                )}
              </div>
            )}
          </div>
          <span onClick={() => setModal('settings')} className="ns-user-display" style={{cursor:'pointer'}}>
              Settings {me.display_name || 'User'}
          </span>
          <button onClick={onLogout} className="ns-btn-text">Logout</button>
        </div>
      </header>

      <main className="ns-main">
        <aside className={`ns-sidebar ${sidebarOpen ? 'mobile-open' : ''}`}>
          <button className="ns-btn primary full" onClick={createThread}>+ New Thread</button>
          <button className="ns-btn secondary full" style={{marginTop:'8px'}} onClick={loadTodayView}>Today</button>
          <button className="ns-btn secondary full" style={{marginTop:'8px'}} onClick={() => { setView("life-admin"); setSidebarOpen(false); }}>Life Admin</button>
          <button className="ns-btn secondary full" style={{marginTop:'8px'}} onClick={loadMemories}>Memory Vault</button>
          <button className="ns-btn secondary full ns-mobile-settings-btn" style={{marginTop:'8px'}} onClick={() => { setModal('settings'); setSidebarOpen(false); }}>Settings</button>
          
          <div className="ns-thread-bulk-bar">
            <button
              type="button"
              onClick={() => {
                if (bulkThreadMode) clearBulkThreadSelection();
                else {
                  setMenuOpenFor(null);
                  setBulkThreadMode(true);
                }
              }}
            >
              {bulkThreadMode ? "Cancel" : "Select"}
            </button>
            {bulkThreadMode && (
              <button
                type="button"
                className="danger"
                onClick={() => setBulkDeleteOpen(true)}
                disabled={!selectedDeletableThreads.length}
              >
                Delete {selectedDeletableThreads.length || ""}
              </button>
            )}
          </div>

          <div className="ns-thread-list">
            {threads.map(t => {
              const selected = selectedThreadIds.has(String(t.id));
              return (
                <div key={t.id} className={`ns-thread-item ${activeThreadId === t.id ? 'active' : ''} ${selected ? 'selected' : ''} ${bulkThreadMode ? 'selecting' : ''}`} onClick={() => selectThread(t.id)}>
                  {bulkThreadMode && (
                    <input
                      type="checkbox"
                      className="ns-thread-check"
                      checked={selected}
                      disabled={t.is_locked}
                      onChange={() => toggleThreadSelection(t.id)}
                      onClick={e => e.stopPropagation()}
                      aria-label={`Select ${t.title}`}
                    />
                  )}
                  <span className="ns-thread-title">
                      {t.is_locked && <span className="ns-thread-lock-label">Locked</span>}
                      {t.title}
                  </span>
                  {!bulkThreadMode && (
                    <button className="ns-more-btn" onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenFor(menuOpenFor === t.id ? null : t.id);
                    }}>...</button>
                  )}

                  {menuOpenFor === t.id && (
                    <div className="ns-menu">
                      <button onClick={(e) => { e.stopPropagation(); setRenamingThread(t); setNewName(t.title); setMenuOpenFor(null); }}>Rename</button>
                      <button onClick={(e) => { e.stopPropagation(); toggleLock(t); }}>{t.is_locked ? 'Unlock' : 'Lock'}</button>
                      <button className="delete" onClick={(e) => { e.stopPropagation(); setDeletingThread(t); setMenuOpenFor(null); }}>Delete</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {sidebarOpen && <div className="ns-sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

        <section className="ns-content">
          {view === "today" ? (
            <div className="ns-today-view">
              <div className="ns-today-header">
                <div>
                  <h2>Today</h2>
                  <p>Your briefing, tasks, and unread items.</p>
                </div>
                <button className="ns-btn secondary" onClick={loadTodayData} disabled={paLoading}>{paLoading ? "Refreshing..." : "Refresh"}</button>
              </div>

              {todayLoadError && (
                <div className="ns-today-warning" role="status">
                  <div>
                    <strong>Northstar couldn't refresh everything</strong>
                    <span>{todayLoadError}</span>
                  </div>
                  <button type="button" onClick={loadTodayData} disabled={paLoading}>Try again</button>
                </div>
              )}

              <LifeAdminToday
                calendar={getBriefingSection(paBriefing, ["calendar", "calendar_summary", "events", "schedule"])}
                onOpen={() => setView("life-admin")}
              />

              <div className="ns-today-grid">
                <section className="ns-today-card briefing-card ns-day-card">
                  <div className="ns-today-card-head">
                    <div><span className="ns-kicker">At a glance</span><h3>Your day</h3></div>
                    <span>{paDashboard?.period || "today"}</span>
                  </div>
                  {paBriefing && (
                    <div className="ns-today-overview" aria-label="Today at a glance">
                      <div><strong>{getBriefingSection(paBriefing, ["calendar", "calendar_summary", "events", "schedule"]).length}</strong><span>appointments</span></div>
                      <div className={getVisibleReplyEmails(paBriefing, emailActionState).length ? "needs-attention" : ""}><strong>{getVisibleReplyEmails(paBriefing, emailActionState).length}</strong><span>need a reply</span></div>
                      <div className={paDashboard?.overdue_tasks?.length ? "needs-attention" : ""}><strong>{paDashboard?.overdue_tasks?.length || 0}</strong><span>overdue tasks</span></div>
                    </div>
                  )}
                  {paDashboard?.calendar_intelligence?.length > 0 && (
                    <section className="ns-calendar-intelligence">
                      <div className="ns-immediate-head"><div><span className="ns-kicker">Calendar check</span><h4>Worth sorting</h4></div><span>{paDashboard.calendar_intelligence.length}</span></div>
                      <div className="ns-calendar-alerts">
                        {paDashboard.calendar_intelligence.map(insight => (
                          <article key={insight.id} className={`ns-calendar-alert ${insight.severity}`}>
                            <div className="ns-calendar-alert-copy">
                              <strong>{insight.title}</strong><small>{insight.detail}</small>
                              {calendarInsightEditor?.insightId === insight.id && (
                                <div className="ns-calendar-resolver">
                                  {calendarInsightEditor.kind === "missing_location" ? (
                                    <label>
                                      Location
                                      <input
                                        type="text"
                                        value={calendarInsightEditor.location}
                                        onChange={event => setCalendarInsightEditor(previous => ({ ...previous, location: event.target.value, error: "" }))}
                                        placeholder="Enter the event location"
                                      />
                                    </label>
                                  ) : (
                                    <>
                                      <label>
                                        Event to move
                                        <select
                                          value={calendarInsightEditor.eventId}
                                          onChange={event => {
                                            const selected = calendarInsightEditor.events.find(item => String(item.id) === event.target.value);
                                            setCalendarInsightEditor(previous => ({ ...previous, eventId: event.target.value, startAt: selected?.start_at?.slice(0, 16) || "", error: "" }));
                                          }}
                                        >
                                          {calendarInsightEditor.events.map(event => <option key={event.id} value={event.id}>{event.title}</option>)}
                                        </select>
                                      </label>
                                      <label>
                                        New date and time
                                        <input type="datetime-local" value={calendarInsightEditor.startAt} onChange={event => setCalendarInsightEditor(previous => ({ ...previous, startAt: event.target.value, error: "" }))} />
                                      </label>
                                    </>
                                  )}
                                  {calendarInsightEditor.error && <small className="ns-action-error">{calendarInsightEditor.error}</small>}
                                  <div>
                                    <button type="button" onClick={saveCalendarInsight} disabled={calendarInsightEditor.saving}>{calendarInsightEditor.saving ? "Saving..." : "Save"}</button>
                                    <button type="button" className="secondary" onClick={() => setCalendarInsightEditor(null)} disabled={calendarInsightEditor.saving}>Cancel</button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="ns-calendar-alert-actions">
                              <button type="button" onClick={() => handleCalendarInsight(insight)}>{insight.kind === "preparation" ? "Plan" : "Resolve"}</button>
                              <button type="button" className="secondary" onClick={() => ignoreCalendarInsight(insight)}>
                                {insight.kind === "clash" ? "Ignore clash" : insight.kind === "missing_location" ? "Ignore location" : "Ignore"}
                              </button>
                            </div>
                          </article>
                        ))}
                      </div>
                    </section>
                  )}
                  {paDashboard?.priority_queue?.length > 0 && (
                    <section className="ns-immediate-actions">
                      <div className="ns-immediate-head">
                        <div><span className="ns-kicker">Prioritised for you</span><h4>Needs attention</h4></div>
                        <span>{paDashboard.priority_queue.length}</span>
                      </div>
                      <div className="ns-immediate-list">
                        {paDashboard.priority_queue.slice(0, 6).map(item => (
                          <article key={item.id} className={`ns-immediate-item ${item.urgency || "soon"}`}>
                            <div className="ns-immediate-copy">
                              <span className="ns-source-label">{item.source}</span>
                              <strong>{item.title}</strong>
                              <small>{item.detail}</small>
                            </div>
                            <div className="ns-immediate-actions">
                              {item.actions?.slice(0, 3).map(action => (
                                <button type="button" key={action} className={action === "complete" || action === "review_reply" ? "primary" : ""} onClick={() => handlePriorityAction(item, action)}>
                                  {priorityActionLabel(action)}
                                </button>
                              ))}
                            </div>
                          </article>
                        ))}
                      </div>
                    </section>
                  )}
                  {paBriefing ? (
                    <div className="ns-briefing-wrap">
                      {Array.isArray(paBriefing.priorities) && paBriefing.priorities.length > 0 && (
                        <section className="ns-pa-feature">
                          <span className="ns-kicker">Actions for today</span>
                          <ol className="ns-priority-list">
                            {paBriefing.priorities.map((item, index) => (
                              <li key={`${item.kind}-${index}`}>
                                <span className={`ns-priority-dot ${item.priority || "medium"}`}></span>
                                <div><strong>{item.title}</strong><small>{item.detail}</small></div>
                              </li>
                            ))}
                          </ol>
                        </section>
                      )}

                      {Array.isArray(paBriefing.waiting_for_replies) && paBriefing.waiting_for_replies.length > 0 && (
                        <section className="ns-pa-feature ns-follow-up-queue">
                          <span className="ns-kicker">Follow-up queue</span>
                          {paBriefing.waiting_for_replies.map(item => (
                            <article key={item.id} className="ns-follow-up-card">
                              <div><strong>{item.subject}</strong><small>{item.recipient} · waiting {item.days_waiting} days</small></div>
                              <textarea rows="4" value={followUpDrafts[item.id] || ""} onChange={event => setFollowUpDrafts(previous => ({ ...previous, [item.id]: event.target.value }))} aria-label={`Follow-up message for ${item.subject}`} />
                              <div className="ns-follow-up-actions">
                                <button type="button" className="ns-btn secondary" onClick={() => handleFollowUp(item, "draft")} disabled={["saving", "sending", "sent"].includes(followUpState[item.id])}>{followUpState[item.id] === "saving" ? "Saving..." : "Save draft"}</button>
                                <button type="button" className="ns-btn primary" onClick={() => handleFollowUp(item, "send")} disabled={["saving", "sending", "sent"].includes(followUpState[item.id])}>{followUpState[item.id] === "sending" ? "Sending..." : followUpState[item.id] === "sent" ? "Sent" : "Review & send"}</button>
                                {followUpState[item.id] === "drafted" && <span>Saved in drafts</span>}
                                {followUpState[item.id] === "error" && <span className="ns-action-error">Not sent</span>}
                              </div>
                            </article>
                          ))}
                        </section>
                      )}

                      {Array.isArray(paBriefing.meeting_preparations) && paBriefing.meeting_preparations.length > 0 && (
                        <section className="ns-pa-feature">
                          <span className="ns-kicker">Meeting preparation</span>
                          {paBriefing.meeting_preparations.map((meeting, index) => (
                            <details key={`${meeting.title}-${index}`} className="ns-meeting-prep">
                              <summary><strong>{meeting.time} {meeting.title}</strong><span>{meeting.location || "View preparation"}</span></summary>
                              {meeting.attendees?.length > 0 && <p>With {meeting.attendees.map(person => person.name).join(", ")}</p>}
                              <ul>{meeting.talking_points?.map((point, pointIndex) => <li key={pointIndex}>{point}</li>)}</ul>
                            </details>
                          ))}
                        </section>
                      )}

                      {paBriefing.end_of_day && (
                        <section className="ns-pa-feature ns-end-day">
                          <span className="ns-kicker">End-of-day review</span>
                          <p>{paBriefing.end_of_day.summary}</p>
                          <div>
                            <span><strong>{paBriefing.end_of_day.open_tasks}</strong> open tasks</span>
                            <span><strong>{paBriefing.end_of_day.waiting_for_replies}</strong> waiting replies</span>
                          </div>
                        </section>
                      )}
                      {paDashboard?.relationships?.some(item => item.needs_attention) && (
                        <section className="ns-pa-feature ns-people-attention">
                          <span className="ns-kicker">People to remember</span>
                          <div className="ns-relationship-list">
                            {paDashboard.relationships.filter(item => item.needs_attention).slice(0, 4).map(contact => (
                              <article key={contact.id} className="needs-attention">
                                <div><strong>{contact.name}</strong><small>{contact.organisation || contact.relationship}</small><span>{contact.recommendation}</span></div>
                                <button type="button" onClick={() => openContactCheckIn(contact)}>Check in</button>
                              </article>
                            ))}
                          </div>
                        </section>
                      )}

                      <section className="ns-email-triage">
                        <div className="ns-email-triage-head">
                          <div>
                            <span className="ns-kicker">Decision queue</span>
                            <h4>Needs your reply</h4>
                          </div>
                          <span>{getVisibleReplyEmails(paBriefing, emailActionState).length}</span>
                        </div>
                        {paBriefing.compose_permission_required && (
                          <div className="ns-permission-note">
                            <p>Reconnect Google once to let Northstar save drafts or send only after you approve.</p>
                            <button type="button" onClick={() => connectGoogle("calendar,mail")}>Reconnect Google</button>
                          </div>
                        )}
                        {getVisibleReplyEmails(paBriefing, emailActionState).length ? (
                          <div className="ns-email-cards">
                            {getVisibleReplyEmails(paBriefing, emailActionState).map(email => {
                              const actionState = emailActionState[email.id];
                              const busy = actionState === "saving" || actionState === "sending" || actionState === "ignoring" || actionState === "snoozing";
                              return (
                                <article key={email.id} className="ns-email-card">
                                  <div className="ns-email-meta">
                                    <span className={`ns-priority ${email.priority || "medium"}`}>{email.priority || "medium"}</span>
                                    <small>{email.account}</small>
                                  </div>
                                  <h5>{email.subject}</h5>
                                  <p className="ns-email-from">From {email.from}</p>
                                  <p className="ns-email-snippet">{email.snippet}</p>
                                  {email.reason && <p className="ns-email-reason">{email.reason}</p>}
                                  <label htmlFor={`reply-${email.id}`}>Suggested reply</label>
                                  <textarea
                                    id={`reply-${email.id}`}
                                    value={emailReplyDrafts[email.id] || ""}
                                    onChange={event => setEmailReplyDrafts(previous => ({ ...previous, [email.id]: event.target.value }))}
                                    rows="5"
                                    placeholder="Write your reply..."
                                  />
                                  <div className="ns-email-actions">
                                    <button type="button" className="ns-btn secondary" onClick={() => handleEmailReplyAction(email, "draft")} disabled={busy || actionState === "sent" || !email.can_compose}>
                                      {actionState === "saving" ? "Saving..." : "Save draft"}
                                    </button>
                                    <button type="button" className="ns-btn primary" onClick={() => handleEmailReplyAction(email, "send")} disabled={busy || actionState === "sent" || !email.can_compose}>
                                      {actionState === "sending" ? "Sending..." : "Review & send"}
                                    </button>
                                    <button type="button" className="ns-btn secondary ns-ignore-button" onClick={() => handleEmailIgnore(email)} disabled={busy || actionState === "sent"}>
                                      {actionState === "ignoring" ? "Ignoring..." : "Ignore"}
                                    </button>
                                    {email.provider !== "imap" && <button type="button" className="ns-btn secondary" onClick={() => setEmailSnoozeOpen(emailSnoozeOpen === email.id ? null : email.id)} disabled={busy}>
                                      {actionState === "snoozing" ? "Snoozing..." : "Snooze"}
                                    </button>}
                                    {email.provider !== "imap" && emailSnoozeOpen === email.id && (
                                      <div className="ns-snooze-options">
                                        <button type="button" onClick={() => handleEmailSnooze(email, 1)}>Tomorrow</button>
                                        <button type="button" onClick={() => handleEmailSnooze(email, 7)}>Next week</button>
                                        <label>Choose date<input type="date" min={new Date().toISOString().slice(0, 10)} onChange={event => {
                                          if (!event.target.value) return;
                                          const chosen = new Date(`${event.target.value}T09:00:00`);
                                          const days = Math.max(1, Math.ceil((chosen - new Date()) / 86400000));
                                          handleEmailSnooze(email, days);
                                        }} /></label>
                                      </div>
                                    )}
                                    {actionState === "drafted" && <span className="ns-action-success">Saved in drafts</span>}
                                    {actionState === "sent" && <span className="ns-action-success">Sent</span>}
                                    {actionState === "error" && <span className="ns-action-error">Not sent</span>}
                                  </div>
                                </article>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="ns-muted">No recent emails appear to need a reply.</p>
                        )}
                      </section>
                      <div className="ns-briefing-sections">
                        {getBriefingSections(paBriefing).map(({ label, description, items }) => (
                          <div key={label} className="ns-briefing-section">
                            <h4>{label}</h4>
                            <p className="ns-briefing-description">{description}</p>
                            {renderBriefingItems(items, label)}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="ns-muted">Refresh to summarise today's calendar and important email signals.</p>
                  )}
                </section>

                <details className="ns-today-card ns-pa-desk ns-today-secondary">
                  <summary className="ns-secondary-summary"><span><b>PA Desk</b><small>Commitments, contacts and personal admin</small></span><em>{paItems.length}</em></summary>
                  <p className="ns-muted">Commitments, important people, renewals and actions waiting for you.</p>
                  <form className="ns-pa-item-form" onSubmit={createPaItem}>
                    <select value={newPaItem.kind} onChange={event => setNewPaItem(previous => ({ ...previous, kind: event.target.value }))}><option value="commitment">Commitment</option><option value="contact">Important contact</option><option value="admin">Personal admin</option><option value="approval">Approval</option></select>
                    <input value={newPaItem.title} onChange={event => setNewPaItem(previous => ({ ...previous, title: event.target.value }))} placeholder="What should Northstar remember?" />
                    <input type="datetime-local" value={newPaItem.due_at} onChange={event => setNewPaItem(previous => ({ ...previous, due_at: event.target.value }))} />
                    <select value={newPaItem.priority} onChange={event => setNewPaItem(previous => ({ ...previous, priority: event.target.value }))}><option value="high">High priority</option><option value="medium">Normal priority</option><option value="low">Low priority</option></select><button className="ns-btn primary" type="submit">Add</button>
                  </form>
                  <div className="ns-pa-item-list">
                    {paItems.length ? paItems.map(item => (
                      <article key={item.id} className="ns-pa-item">
                        <div><span className={`ns-priority ${item.priority || "medium"}`}>{item.kind}</span><strong>{item.title}</strong>
                          {(item.person_name || item.organisation) && <small>{item.person_name || item.organisation}</small>}
                          {item.due_at && <small>Due {new Date(item.due_at).toLocaleString()}</small>}{item.details && <p>{item.details}</p>}</div>
                        <div className="ns-pa-item-actions">{item.requires_approval && <button type="button" onClick={() => resolvePaItem(item, "approve")}>Approve</button>}<button type="button" onClick={() => resolvePaItem(item, "complete")}>{item.kind === "contact" ? "Archive" : "Done"}</button></div>
                      </article>
                    )) : <p className="ns-muted">Nothing waiting. Northstar will keep this desk focused.</p>}
                  </div>
                </details>

                <section className="ns-today-card ns-reminders-card ns-today-priority">
                  <div className="ns-today-card-head">
                    <div><span className="ns-kicker">Stay ahead</span><h3>Upcoming Reminders</h3></div>
                    <span>{paReminders.length}</span>
                  </div>
                  <p className="ns-muted">Edit, postpone, repeat or cancel anything Northstar is due to remind you about.</p>
                  <div className="ns-reminder-list">
                    {paReminders.length ? groupReminders(paReminders).map(group => (
                      <details key={group.key} className="ns-reminder-group" open={group.open}>
                        <summary><span>{group.title}</span><small>{group.reminders.length}</small></summary>
                        <div className="ns-reminder-group-items">
                          {group.reminders.map(reminder => (
                            <article key={reminder.id} className="ns-reminder-item">
                              <div className="ns-reminder-copy">
                                <strong>{reminder.title}</strong>
                                <small>{formatReminderDate(reminder.next_run_at || reminder.due_at, timeFormat)}</small>
                                {reminder.repeat && reminder.repeat !== "once" && <span className="ns-repeat-badge">Repeats {reminder.repeat}</span>}
                              </div>
                              <div className="ns-reminder-actions">
                                <button type="button" onClick={() => openReminderEditor(reminder)}>Edit</button>
                                <button type="button" onClick={() => postponeReminder(reminder, 1440)}>Tomorrow</button>
                                <button type="button" onClick={() => postponeReminder(reminder, 10080)}>Next week</button>
                                <button type="button" className="ns-reminder-cancel" onClick={() => cancelReminder(reminder)}>Cancel</button>
                              </div>
                            </article>
                          ))}
                        </div>
                      </details>
                    )) : <p className="ns-muted">No upcoming reminders. Ask Northstar to remind you whenever you need.</p>}
                  </div>
                </section>
                <section className="ns-today-card ns-tasks-card">
                  <div className="ns-today-card-head">
                    <h3>Tasks</h3>
                    <span>{paTasks.filter(task => !(task.completed || task.completed_at || task.is_complete)).length}</span>
                  </div>
                  <form className="ns-task-form" onSubmit={createPaTask}>
                    <input value={newTaskText} onChange={e => setNewTaskText(e.target.value)} placeholder="Add a task..." />
                    <button className="ns-btn primary" type="submit">Add</button>
                  </form>
                  <div className="ns-task-list">
                    {paTasks.length ? paTasks.map(task => {
                      const done = task.completed || task.completed_at || task.is_complete;
                      return (
                        <div key={task.id || getItemText(task)} className={`ns-task-item ${done ? "done" : ""}`}>
                          <span>{getItemText(task)}</span>
                          {!done && <button type="button" onClick={() => completePaTask(task)}>Done</button>}
                        </div>
                      );
                    }) : <p className="ns-muted">No tasks yet.</p>}
                  </div>
                </section>

                <details className="ns-today-card settings-card-inline ns-today-secondary">
                  <summary className="ns-secondary-summary"><span><b>What Northstar has learned</b><small>Review and correct Northstar's preferences</small></span></summary>
                  <p className="ns-muted">These choices shape what Northstar shows, chases and leaves alone. You can change or remove them at any time.</p>
                  {paSettings ? (
                    <div className="ns-pa-settings">
                      <label><input type="checkbox" checked={paSettings.daily_briefing_enabled !== false} onChange={e => updatePaSetting("daily_briefing_enabled", e.target.checked)} /> Daily briefing</label>
                      <label><span>Briefing time</span><input type="time" value={normalizeBriefingTime(paSettings.briefing_time)} onChange={e => updatePaSetting("briefing_time", e.target.value)} /></label>
                      {paSettingStatus && <small className={`ns-pa-setting-status ${paSettingStatus === "Saved" ? "saved" : ""}`}>{paSettingStatus}</small>}
                      <label><input type="checkbox" checked={paSettings.midday_review_enabled !== false} onChange={e => updatePaSetting("midday_review_enabled", e.target.checked)} /> Midday review</label>
                      <label><span>Midday time</span><input type="time" value={paSettings.midday_review_time || "12:30"} onChange={e => updatePaSetting("midday_review_time", e.target.value)} /></label>
                      <label><input type="checkbox" checked={paSettings.end_of_day_review_enabled !== false} onChange={e => updatePaSetting("end_of_day_review_enabled", e.target.checked)} /> End-of-day review</label>
                      <label><span>Evening time</span><input type="time" value={paSettings.end_of_day_review_time || "17:30"} onChange={e => updatePaSetting("end_of_day_review_time", e.target.value)} /></label>
                      <label><span>Travel buffer</span><select value={paSettings.travel_buffer_minutes ?? 20} onChange={e => updatePaSetting("travel_buffer_minutes", Number(e.target.value))}><option value="0">None</option><option value="10">10 minutes</option><option value="20">20 minutes</option><option value="30">30 minutes</option><option value="45">45 minutes</option><option value="60">1 hour</option></select></label>
                      <label><span>Chase replies after</span><select value={paSettings.follow_up_days ?? 3} onChange={e => updatePaSetting("follow_up_days", Number(e.target.value))}><option value="1">1 day</option><option value="2">2 days</option><option value="3">3 days</option><option value="5">5 days</option><option value="7">1 week</option><option value="14">2 weeks</option></select></label>
                      <label><span>Relationship nudge</span><select value={paSettings.relationship_nudge_days ?? 30} onChange={e => updatePaSetting("relationship_nudge_days", Number(e.target.value))}><option value="14">After 2 weeks</option><option value="30">After 30 days</option><option value="60">After 60 days</option><option value="90">After 90 days</option></select></label>
                      <label><input type="checkbox" checked={paSettings.include_calendar !== false} onChange={e => updatePaSetting("include_calendar", e.target.checked)} /> Include calendar</label>
                      <label><input type="checkbox" checked={paSettings.include_email !== false} onChange={e => updatePaSetting("include_email", e.target.checked)} /> Include email</label>
                      <label><input type="checkbox" checked={paSettings.include_tasks !== false} onChange={e => updatePaSetting("include_tasks", e.target.checked)} /> Include tasks</label>
                      <label><input type="checkbox" checked={paSettings.include_memory !== false} onChange={e => updatePaSetting("include_memory", e.target.checked)} /> Include memory</label>
                      <div className="ns-setting-subsection">
                        <strong>Your reply style</strong>
                        <small>
                          {paBriefing?.reply_style?.sample_count
                            ? `Learning from ${paBriefing.reply_style.sample_count} amended repl${paBriefing.reply_style.sample_count === 1 ? "y" : "ies"} � kept local`
                            : "Amend and send replies to teach Northstar your style. Preferences stay local."}
                        </small>
                      </div>
                      <div className="ns-setting-subsection">
                        <strong>Senders Northstar leaves alone</strong>
                        {ignoredSenders.length ? ignoredSenders.map(rule => (
                          <div key={rule.id} className="ns-ignored-sender">
                            <span>{rule.sender_name || rule.sender_email}<small>{rule.sender_email}</small></span>
                            <button type="button" onClick={() => restoreIgnoredSender(rule)}>Restore</button>
                          </div>
                        )) : <small>No ignored senders.</small>}
                      </div>
                    </div>
                  ) : <p className="ns-muted">Settings unavailable.</p>}
                </details>
              </div>
            </div>
          ) : view === "life-admin" ? (
            <LifeAdminView requestConfirmation={requestConfirmation} />
          ) : view === "memory" ? (
            <div className="ns-memory-view">
              <div className="ns-vault-header">
                <h2>Memory Vault</h2>
                <p>Facts Northstar has learned about you.</p>
              </div>

              <div className="ns-memory-grid">
                {memories.map((m) => (
                  <div key={m.id} className="ns-memory-card">
                    <div className="ns-mem-content">
                      <div className="ns-mem-key">{m.key}</div>
                      <div className="ns-mem-val">{m.value}</div>
                    </div>
                    <button onClick={() => deleteMem(m.id)} className="ns-btn-forget" title="Remove from memory">Forget</button>
                  </div>
                ))}
              </div>

              <div className="ns-vault-actions">
                <button className="ns-btn secondary" onClick={() => setView("chat")}>← Back to Chat</button>
              </div>
            </div>
          ) : (
            <div className="ns-chatpanel">
              {activeThreadId ? (
                <>
                  <div className="ns-messages">
                    {messages.map((m, i) => (
                      <div key={i} className={`ns-msg ${m.role}`}>
                        <div className="ns-msg-meta">
                          <span>{m.role === 'user' ? 'You' : 'Northstar'}</span>
                          {m.role === "assistant" && (
                            <button
                              type="button"
                              className="ns-listen-reply"
                              onClick={() => {
                                unlockSpeechSynthesis();
                                speakAssistantReply(m.content, true);
                              }}
                              title="Listen to this reply"
                              aria-label="Listen to this reply"
                            >
                              Listen
                            </button>
                          )}
                        </div>
                        <div className="ns-msg-text">
                          {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  <form className={`ns-composer ${isListening ? "listening" : ""}`} onSubmit={onSendMessage}>
                    <div className="ns-composer-field">
                      <textarea 
                        className="ns-textarea" 
                        value={draft} 
                        onChange={e => setDraft(e.target.value)} 
                        onKeyDown={handleKeyDown}
                        placeholder={loading ? "Thinking..." : isListening ? "Listening..." : "Message or tap Mic..."}
                        disabled={loading || isListening}
                      />
                      {voiceStatus && <div className="ns-voice-status">{voiceStatus}</div>}
                    </div>
                    <button
                      className={`ns-btn ns-voice-btn ${isListening ? "active" : ""}`}
                      type="button"
                      onClick={handleVoiceButton}
                      disabled={loading}
                      aria-pressed={isListening}
                      title={isListening ? "Stop and send" : "Speak to Northstar"}
                    >
                      {isListening ? (
                        <span className="ns-voice-label">Stop</span>
                      ) : (
                        <svg className="ns-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Z" />
                          <path d="M19 11a7 7 0 0 1-14 0" />
                          <path d="M12 18v4" />
                          <path d="M8 22h8" />
                        </svg>
                      )}
                    </button>
                    <button
                      className={`ns-btn ns-speak-toggle ${speakReplies ? "active" : ""}`}
                      type="button"
                      onClick={() => {
                        const next = !speakReplies;
                        if (next) unlockSpeechSynthesis();
                        else stopSpeech();
                        setSpeakReplies(next);
                        setVoiceAutoPlay(next);
                        setEditVoiceAutoPlay(next);
                        apiPatch("/voice/settings", { voice: voiceStyle, auto_play: next }).catch(() => {});
                      }}
                      title={speakReplies ? "Automatic read aloud is on" : "Automatic read aloud is off"}
                    >
                      {speakReplies ? "Read aloud on" : "Read aloud off"}
                    </button>
                    <button className="ns-btn primary" type="submit" disabled={loading || isListening}>Send</button>
                  </form>
                </>
              ) : (
                <div className="ns-empty-state">
                    <h2>Welcome back.</h2>
                    <p>Start a new conversation or view your vault.</p>
                </div>
              )}
            </div>
          )}
        </section>
      </main>

      {updateRequired && (
        <div className="ns-modal-overlay ns-update-overlay" role="presentation">
          <div
            className="ns-modal ns-update-card"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="ns-update-title"
            aria-describedby="ns-update-description"
          >
            <img className="ns-update-mark" src="/logo192.png" alt="" aria-hidden="true" />
            <span className="ns-confirm-kicker">Northstar PA</span>
            <h3 id="ns-update-title">Northstar has been updated</h3>
            <p id="ns-update-description">
              A newer version is ready. Refresh now to continue with the latest features and fixes.
            </p>
            <div className="ns-modal-actions">
              <button type="button" className="ns-btn primary full" onClick={reloadForUpdate} autoFocus>
                Refresh Northstar
              </button>
            </div>
          </div>
        </div>
      )}


      {contactCheckIn && (
        <div className="ns-modal-overlay" role="presentation" onMouseDown={() => setContactCheckIn(null)}>
          <form className="ns-modal ns-contact-checkin" role="dialog" aria-modal="true" aria-labelledby="ns-contact-checkin-title" onSubmit={saveContactCheckIn} onMouseDown={event => event.stopPropagation()}>
            <div className="ns-modal-header"><span className="ns-confirm-kicker">Northstar PA</span><h3 id="ns-contact-checkin-title">Check in with {contactCheckIn.name}</h3><p className="ns-modal-subtitle">Record the contact and optionally plan when to reconnect.</p></div>
            <label><span>What happened?</span><textarea value={contactCheckIn.notes} onChange={event => setContactCheckIn(previous => ({ ...previous, notes: event.target.value }))} placeholder="Conversation, promise, or useful detail" rows="4" autoFocus /></label>
            <label><span>Next check-in</span><input type="datetime-local" value={contactCheckIn.next_contact_at} onChange={event => setContactCheckIn(previous => ({ ...previous, next_contact_at: event.target.value }))} /></label>
            <div className="ns-modal-actions"><button type="button" className="ns-btn secondary" onClick={() => setContactCheckIn(null)}>Cancel</button><button type="submit" className="ns-btn primary">Save check-in</button></div>
          </form>
        </div>
      )}
      {reminderEditor && (
        <div className="ns-modal-overlay" role="presentation" onMouseDown={() => !reminderSaving && setReminderEditor(null)}>
          <form className="ns-modal ns-reminder-editor" role="dialog" aria-modal="true" aria-labelledby="ns-reminder-editor-title" onSubmit={saveReminder} onMouseDown={event => event.stopPropagation()}>
            <div className="ns-modal-header">
              <span className="ns-confirm-kicker">Northstar PA</span>
              <h3 id="ns-reminder-editor-title">Edit reminder</h3>
              <p className="ns-modal-subtitle">Change what Northstar should remind you about and when.</p>
            </div>
            <label><span>Reminder</span><input value={reminderEditor.title} onChange={event => setReminderEditor(previous => ({ ...previous, title: event.target.value }))} autoFocus required /></label>
            <label><span>Date and time</span><input type="datetime-local" value={reminderEditor.remind_at} onChange={event => setReminderEditor(previous => ({ ...previous, remind_at: event.target.value }))} required /></label>
            <label><span>Repeat</span><select value={reminderEditor.repeat} onChange={event => setReminderEditor(previous => ({ ...previous, repeat: event.target.value }))}><option value="once">Once</option><option value="daily">Every day</option><option value="weekly">Every week</option><option value="monthly">Every month</option></select></label>
            <div className="ns-modal-actions">
              <button type="button" className="ns-btn secondary" disabled={reminderSaving} onClick={() => setReminderEditor(null)}>Close</button>
              <button type="submit" className="ns-btn primary" disabled={reminderSaving}>{reminderSaving ? "Saving..." : "Save reminder"}</button>
            </div>
          </form>
        </div>
      )}
      {confirmDialog && (
        <div className="ns-modal-overlay ns-confirm-overlay" role="presentation" onMouseDown={() => closeConfirmation(false)}>
          <div
            className="ns-modal ns-confirm-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ns-confirm-title"
            onMouseDown={event => event.stopPropagation()}
          >
            <span className="ns-confirm-kicker">Northstar PA</span>
            <h3 id="ns-confirm-title">{confirmDialog.title}</h3>
            <p>{confirmDialog.message}</p>
            <div className="ns-modal-actions">
              <button type="button" className="ns-btn secondary" onClick={() => closeConfirmation(false)}>Cancel</button>
              <button
                type="button"
                className={`ns-btn primary ${confirmDialog.tone === "danger" ? "ns-danger-btn" : ""}`}
                onClick={() => closeConfirmation(true)}
                autoFocus
              >
                {confirmDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {renamingThread && (
        <div className="ns-modal-overlay">
          <div className="ns-modal">
            <h3>Rename</h3>
            <input className="ns-auth-input" value={newName} onChange={e => setNewName(e.target.value)} autoFocus />
            <div className="ns-modal-actions">
              <button className="ns-btn secondary" onClick={() => setRenamingThread(null)}>Cancel</button>
              <button className="ns-btn primary" onClick={handleRename}>Save</button>
            </div>
          </div>
        </div>
      )}

      {deletingThread && (
        <div className="ns-modal-overlay">
          <div className="ns-modal">
            <h3>Delete Thread?</h3>
            {deletingThread.is_locked ? (
              <p className="ns-lock-warning">Thread is locked. Unlock it first.</p>
            ) : (
              <p>This will permanently remove the history.</p>
            )}
            <div className="ns-modal-actions">
              <button className="ns-btn secondary" onClick={() => setDeletingThread(null)}>Cancel</button>
              {!deletingThread.is_locked && <button className="ns-btn primary ns-danger-btn" onClick={confirmDelete}>Delete</button>}
            </div>
          </div>
        </div>
      )}

      {bulkDeleteOpen && (
        <div className="ns-modal-overlay">
          <div className="ns-modal">
            <h3>Delete Selected Threads?</h3>
            <p>
              This will permanently delete {selectedDeletableThreads.length} thread{selectedDeletableThreads.length === 1 ? "" : "s"}.
              {selectedLockedCount > 0 && ` ${selectedLockedCount} locked thread${selectedLockedCount === 1 ? "" : "s"} will be skipped.`}
            </p>
            <div className="ns-modal-actions">
              <button className="ns-btn secondary" onClick={() => setBulkDeleteOpen(false)} disabled={bulkDeleting}>Cancel</button>
              <button className="ns-btn primary ns-danger-btn" onClick={confirmBulkDelete} disabled={bulkDeleting || !selectedDeletableThreads.length}>
                {bulkDeleting ? "Deleting..." : "Delete Selected"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal === 'google-services' && (
        <div className="ns-modal-overlay">
          <div className="ns-modal google-services-card">
            <div className="ns-modal-header">
              <h3>Connect Google</h3>
              <p className="ns-modal-subtitle">Choose what Northstar should be allowed to use.</p>
            </div>
            <div className="ns-google-choice modal-choice">
              <button type="button" onClick={() => connectGoogle("mail")}>
                <strong>Email only</strong>
                <span>Summarise Gmail, prepare drafts, and send only after approval.</span>
              </button>
              <button type="button" onClick={() => connectGoogle("calendar")}>
                <strong>Calendar only</strong>
                <span>View your calendar and add events when you ask.</span>
              </button>
              <button type="button" onClick={() => connectGoogle("calendar,mail")}>
                <strong>Calendar and email</strong>
                <span>Full daily briefing with approved Gmail replies.</span>
              </button>
            </div>
            <div className="ns-modal-actions">
              <button className="ns-btn secondary" onClick={() => setModal('settings')}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {modal === 'imap-connect' && (
        <div className="ns-modal-overlay"><div className="ns-modal settings-card ns-imap-modal">
          <div className="ns-modal-header"><h3>Connect email</h3><p className="ns-modal-subtitle">Use IMAP for incoming mail and SMTP for approved replies. An app password is recommended.</p></div>
          <form onSubmit={connectImap}>
            <div className="ns-mail-presets"><button type="button" onClick={() => applyMailPreset("yahoo")}>Yahoo</button><button type="button" onClick={() => applyMailPreset("icloud")}>iCloud</button><button type="button" onClick={() => applyMailPreset("other")}>Other</button></div>
            <div className="ns-imap-grid">
              <label>Email address<input type="email" required value={imapForm.email} onChange={event => setImapForm(previous => ({ ...previous, email: event.target.value, username: previous.username || event.target.value }))} /></label>
              <label>Username<input required value={imapForm.username} onChange={event => setImapForm(previous => ({ ...previous, username: event.target.value }))} /></label>
              <label className="wide">App password<input type="password" required autoComplete="new-password" value={imapForm.password} onChange={event => setImapForm(previous => ({ ...previous, password: event.target.value }))} /></label>
              <label>IMAP server<input required placeholder="imap.example.com" value={imapForm.imap_host} onChange={event => setImapForm(previous => ({ ...previous, imap_host: event.target.value }))} /></label>
              <label>IMAP port<input type="number" required value={imapForm.imap_port} onChange={event => setImapForm(previous => ({ ...previous, imap_port: event.target.value }))} /></label>
              <label>IMAP security<select value={imapForm.imap_security} onChange={event => setImapForm(previous => ({ ...previous, imap_security: event.target.value }))}><option value="ssl">SSL/TLS</option><option value="starttls">STARTTLS</option></select></label>
              <label>SMTP server<input required placeholder="smtp.example.com" value={imapForm.smtp_host} onChange={event => setImapForm(previous => ({ ...previous, smtp_host: event.target.value }))} /></label>
              <label>SMTP port<input type="number" required value={imapForm.smtp_port} onChange={event => setImapForm(previous => ({ ...previous, smtp_port: event.target.value }))} /></label>
              <label>SMTP security<select value={imapForm.smtp_security} onChange={event => setImapForm(previous => ({ ...previous, smtp_security: event.target.value }))}><option value="ssl">SSL/TLS</option><option value="starttls">STARTTLS</option></select></label>
              <label>Inbox folder<input value={imapForm.folder} onChange={event => setImapForm(previous => ({ ...previous, folder: event.target.value }))} /></label>
              <label>Drafts folder<input value={imapForm.drafts_folder} onChange={event => setImapForm(previous => ({ ...previous, drafts_folder: event.target.value }))} /></label>
            </div>
            {imapStatus && <div className={`ns-imap-status ${imapStatusKind}`} role={imapStatusKind === "error" ? "alert" : "status"}><strong>{imapStatusKind === "success" ? "Connection working" : imapStatusKind === "error" ? "Connection failed" : "Checking connection"}</strong><span>{imapStatus}</span></div>}
            <div className="ns-modal-actions"><button type="button" className="ns-btn secondary" onClick={() => setModal('settings')}>Cancel</button><button className="ns-btn primary" type="submit" disabled={imapSaving}>{imapSaving ? "Testing..." : "Test & connect"}</button></div>
          </form>
        </div></div>
      )}

      {modal === 'settings' && (
        <div className="ns-modal-overlay">
          <div className="ns-modal settings-card">
            <div className="ns-modal-header">
              <h3>Settings</h3>
              <p className="ns-modal-subtitle">Keep Northstar configured around you.</p>
            </div>

            <nav className="ns-settings-nav" aria-label="Settings sections">
              {[
                ["general", "General"],
                ["localisation", "Localisation"],
                ["accounts", "Accounts"],
                ["defaults", "Defaults"],
              ].map(([key, label]) => (
                <button type="button" key={key} className={settingsSection === key ? "active" : ""} onClick={() => setSettingsSection(key)}>
                  {label}
                </button>
              ))}
            </nav>

            <div className="ns-form-body ns-settings-body">
              {settingsSection === "general" && (
                <section className="ns-settings-section">
                  <div className="ns-settings-section-head"><h4>General</h4><p>How Northstar addresses and speaks to you.</p></div>
                  <div className="ns-form-group">
                    <label>Nickname</label>
                    <input value={editName} onChange={e => setEditName(e.target.value)} className="ns-auth-input" placeholder="What should I call you?" />
                  </div>
                  <div className="ns-form-group">
                    <label>Spoken reply voice</label>
                    <select value={editVoiceStyle} onChange={e => setEditVoiceStyle(e.target.value)} className="ns-auth-input ns-select">
                      {TTS_VOICE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <label className="ns-checkbox-row">
                      <input type="checkbox" checked={editVoiceAutoPlay} onChange={e => setEditVoiceAutoPlay(e.target.checked)} />
                      <span>Read replies aloud automatically</span>
                    </label>
                    <button type="button" className="ns-btn secondary ns-voice-sample" onClick={playVoiceSample}>Listen to sample</button>
                  </div>
                </section>
              )}

              {settingsSection === "localisation" && (
                <section className="ns-settings-section">
                  <div className="ns-settings-section-head"><h4>Localisation</h4><p>Location, language and time display.</p></div>
                  <div className="ns-form-group">
                    <label>Location</label>
                    <input value={editLoc} onChange={e => setEditLoc(e.target.value)} className="ns-auth-input" placeholder="e.g. Sheffield, UK" />
                    <small className="ns-help-text">Used for local time, weather and travel context.</small>
                  </div>
                  <div className="ns-form-group">
                    <label>Preferred language</label>
                    <select value={editLang} onChange={e => setEditLang(e.target.value)} className="ns-auth-input ns-select">
                      <option value="en">English (UK)</option><option value="es">Spanish</option><option value="fr">French</option><option value="de">German</option>
                    </select>
                  </div>
                  <div className="ns-form-group">
                    <label>Time display</label>
                    <select value={editTimeFormat} onChange={e => setEditTimeFormat(normalizeTimeFormat(e.target.value))} className="ns-auth-input ns-select">
                      {TIME_FORMAT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label} ({option.example})</option>)}
                    </select>
                  </div>
                </section>
              )}

              {settingsSection === "accounts" && (
                <section className="ns-settings-section">
                  <div className="ns-settings-section-head"><h4>Connected accounts</h4><p>Choose which services Northstar can use for each account.</p></div>
                  <div className="ns-account-list">
                    {me.connected_accounts?.length ? me.connected_accounts.map(acc => {
                      const services = getConnectedAccountServices(acc);
                      return (
                        <article key={acc.id} className="ns-integration-card">
                          <div className="ns-integration-main">
                            <span className={`ns-integration-icon ${acc.provider === "google" ? "google" : ""}`}>
                              {acc.provider === "google" ? <img src="/google-g.svg" alt="" aria-hidden="true" /> : "@"}
                            </span>
                            <div className="ns-integration-copy">
                              <div className="ns-integration-provider">{acc.provider === "google" ? "Google" : "IMAP / SMTP"}</div>
                              <div className="ns-integration-email">{acc.email}</div>
                              <div className="ns-account-service-row">
                                <div className="ns-service-badges">{services.map(service => <span key={service}>{service}</span>)}</div>
                                {acc.provider === "google" && (
                                  <label className="ns-account-service-toggle">
                                    <input type="checkbox" checked={services.includes("Calendar")} onChange={event => toggleGoogleCalendar(acc, event.target.checked)} />
                                    <span>Calendar</span>
                                  </label>
                                )}
                                {acc.provider === "imap" && <small className="ns-secure-account-note">Securely stored</small>}
                              </div>
                            </div>
                          </div>
                          <div className="ns-integration-actions">
                            {acc.provider === "google" && !connectedAccountCanWriteCalendar(acc) && <button type="button" className="ns-btn small primary" onClick={() => connectGoogle(services.includes("Email") ? "calendar,mail" : "calendar")}>Calendar permission</button>}
                            {acc.provider === "google" && !connectedAccountCanCompose(acc) && <button type="button" className="ns-btn small primary" onClick={() => connectGoogle("calendar,mail")}>Email permission</button>}
                            {acc.provider === "imap" && <button type="button" className="ns-btn small primary" onClick={() => testImapAccount(acc)} disabled={imapTestState[acc.id]?.state === "testing"}>{imapTestState[acc.id]?.state === "testing" ? "Testing..." : "Test"}</button>}
                            <button type="button" className="ns-btn small secondary ns-disconnect-btn" onClick={() => disconnectAccount(acc.id, acc.provider)}>Disconnect</button>
                            {acc.provider === "imap" && imapTestState[acc.id]?.message && <small className={`ns-account-test ${imapTestState[acc.id].state}`}>{imapTestState[acc.id].message}</small>}
                          </div>
                        </article>
                      );
                    }) : <p className="ns-settings-empty">No accounts connected yet.</p>}
                  </div>
                  <div className="ns-connect-actions">
                    <button className="ns-btn small primary" type="button" onClick={() => setModal("google-services")}>+ Google account</button>
                    <button className="ns-btn small secondary" type="button" onClick={() => { setImapStatus(""); setModal("imap-connect"); }}>+ IMAP / SMTP</button>
                  </div>
                </section>
              )}

              {settingsSection === "defaults" && (
                <section className="ns-settings-section">
                  <div className="ns-settings-section-head"><h4>Default accounts</h4><p>Used whenever you do not name a specific account or calendar.</p></div>
                  <div className="ns-form-group">
                    <label htmlFor="default-calendar">Default calendar</label>
                    <select id="default-calendar" className="ns-auth-input ns-select" value={integrationDefaults.default_calendar_record_id} onChange={event => selectIntegrationDefault("default_calendar_record_id", event.target.value)}>
                      <option value="">Choose a calendar</option>
                      {availableCalendars.map(calendar => <option key={calendar.id} value={calendar.id}>{calendar.calendar_name || calendar.calendar_id}{calendar.account_email ? ` — ${calendar.account_email}` : ""}</option>)}
                    </select>
                    <small className="ns-help-text">New events are added here unless you name another calendar.</small>
                  </div>
                  <div className="ns-form-group">
                    <label htmlFor="default-email">Default email account</label>
                    <select id="default-email" className="ns-auth-input ns-select" value={integrationDefaults.default_email_account_id} onChange={event => selectIntegrationDefault("default_email_account_id", event.target.value)}>
                      <option value="">Choose an email account</option>
                      {(me.connected_accounts || []).filter(acc => getConnectedAccountServices(acc).includes("Email")).map(acc => <option key={acc.id} value={acc.id}>{acc.email}</option>)}
                    </select>
                    <small className="ns-help-text">Used when an email request does not name an account.</small>
                  </div>
                  {integrationDefaultStatus && <small className="ns-default-save-status">{integrationDefaultStatus}</small>}
                </section>
              )}
            </div>

            <div className="ns-modal-actions">
              <button className="ns-btn secondary" type="button" onClick={() => setModal(null)}>Close</button>
              {(settingsSection === "general" || settingsSection === "localisation") && <button className="ns-btn primary" type="button" onClick={saveSettings}>Save changes</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
